// ============================================================================
// Modèle d'une question pour la feuille de brouillon (format d'échange).
// ============================================================================
// Produit HORS LIGNE (génération IA depuis l'énoncé et le corrigé, puis porte
// de validation), stocké dans `scratch_models.model`, lu tel quel par le moteur
// pendant le jeu. Aucune étape n'y est listée : c'est le moteur qui raisonne.
//
// Les outils (`preds`) forment un CATALOGUE FERMÉ : un modèle ne peut citer
// qu'un outil connu du moteur, avec ses paramètres typés. C'est ce qui empêche
// le générateur d'inventer une règle.
// ============================================================================

import { z } from 'zod';
import {
  CLAIM_FORMS,
  E_CONSTANT_RE,
  pathUsesClaimsEuclid,
  pathUsesClaimsFormGestures,
  pathUsesClaimsGestures,
  isFactoredIrreducibleClaim,
  pathUsesClaimsLn,
  pathUsesFractionPrimes,
  textsUseZeroDivision,
} from './kernel-claims-gestures';
import { pathUsesLetterQuotients, textsUseMonomialQuotients } from './kernel-claims-letters';
import { usesKernelPowers } from './kernel-form';
import { pathUsesClaimsPower41 } from './kernel-claims';
import { relForm } from './kernel-poly';
import { parseStatement, relationsIn, statementSymbols, type Term } from './kernel/term';
import { toRatFn } from './kernel/normal';
import { univariateCoefs } from './kernel/witness';
import { usesKernelPythonPlus } from './kernel-python';
import { ENTRY_TEXT_RE } from './kernel/matrix';
import { usesNamedProducts, withNamedProducts } from './named-products';
import { klimNeedsV56, usesDL } from './kernel-limit';

/** les formes et propriétés d'une affirmation (moteur v39, v40) */
export { CLAIM_FORMS };

/**
 * Version du moteur. À incrémenter à chaque changement de comportement du jugement.
 * v2 (10/10/2026) : notation fonctionnelle (P(A), f(2)), ln / exp / cos / sin /
 * tan, π, puissance générale « ^ », valeurs exactes écrites (`exact`). Un modèle
 * v1 se joue à l'identique en v2 ; un moteur v1 refuse un modèle v2.
 */
export const ENGINE_VERSION = 64;
/**
 * v3 (10/10/2026) : congruences (« 2¹⁰⁰ ≡ 2 [7] », mode `congruence`). Une
 * feuille SANS congruence garde la version 2 (les apps installées la jouent).
 */
export const CONGRUENCE_ENGINE_VERSION = 3;
/**
 * v5 (11/10/2026) : comparaisons (« Δ > 0 », « Δ < 0 », « 0 ≤ p ») — lignes
 * `checks` du modèle. Une feuille sans comparaison garde sa version.
 */
export const CHECKS_ENGINE_VERSION = 5;
/**
 * v6 (11/10/2026) : dénombrement — factorielle « 5! », grandeurs « card(A∩B) ».
 * Une feuille qui n'en écrit pas garde sa version.
 */
export const COUNTING_ENGINE_VERSION = 6;
/**
 * v7 (11/10/2026) : remplacer une valeur NÉGATIVE en tête d'un produit
 * (« p = … + (−1) × 1 » pour z₁ × z₂ avec z₁ = −1) est reconnu. Non détectable
 * dans le modèle : la feuille qui en dépend porte la version à la main
 * (`minEngine` des specs du chantier).
 */
export const NEGATIVE_FACTOR_ENGINE_VERSION = 7;
/**
 * v8 (11/10/2026) : une somme (ou un produit) de plus de 4 nombres est comparée
 * (avant : jamais justifiée). Non détectable sur le modèle : `minEngine` par
 * feuille dans les specs du chantier.
 */
export const LONG_SUM_ENGINE_VERSION = 8;
/**
 * v9 (11/10/2026) : le corrigé calcule les fractions sans écriture décimale —
 * quotient de fractions (6 ÷ (2/3) = 9), puissance entière négative (3^(−2) = 1/9),
 * « 1 − 1/6 = 5/6 », puissance d'un produit avec racines ((2√5)² = 20) ; équations à
 * deux solutions : le carré d'une expression est isolé avant de développer
 * ((x − 2)² − 9 = 0 → (x − 2)² = 9), pas de dénominateur chassé dans une seule branche
 * d'un « ou » ((x ÷ 2 − 3)(x + 1) = 0). Juge : calculer dans la ligne qu'on vient
 * d'écrire (« … + 2² » → « … + 4 ») est un pas.
 * Non détectable sur le modèle : `minEngine` par feuille dans les specs du chantier.
 */
export const FRACTION_ENGINE_VERSION = 9;
/**
 * v10 (11/10/2026) : réponses arrondies — champ `rounding` du modèle, ligne
 * « AC ≈ 8,4 » (relation « ≈ », comme une comparaison : jamais un fait d'égalité),
 * dernière étape « Arrondir » du corrigé, choix du QCM comparés à la valeur arrondie.
 * Détectable sur le modèle : une feuille avec `rounding` est en version 10.
 */
export const ROUNDING_ENGINE_VERSION = 10;
/**
 * v11 (11/10/2026) : nombres complexes — mode `complex` du modèle : la feuille
 * manipule i (bout « i », bouts « 1 + i » de la question), chaque maillon jugé en
 * calcul complexe exact et justifié en un pas (développer, i² = −1, découper une
 * puissance, Moivre…). Détectable sur le modèle : une feuille `complex` est en v11.
 */
export const COMPLEX_ENGINE_VERSION = 11;
/**
 * v12 (11/10/2026) : racine d'indice impair d'un négatif — (−27)^(1 ÷ 3) = −3,
 * x³ = −0,125 → x = (−0,125)^(1 ÷ 3) = −0,5 (avant : NaN, puissance réelle).
 * Non détectable sur le modèle : `minEngine: 12` par feuille dans les specs du chantier.
 * Aussi : solution double de l'outil « second degré » (champ `double`, Δ = 0 →
 * x₀ = −b ÷ (2a)) — détectable sur le modèle.
 */
export const ODD_ROOT_ENGINE_VERSION = 12;
/**
 * v13 (11/10/2026) : encadrer une racine carrée — mode `bounds` du modèle :
 * carrés (6² = 36), comparaisons en chaîne (36 < 40 < 49), puis 6 < √40 < 7.
 * Détectable sur le modèle : une feuille `bounds` est en version 13.
 */
export const BOUNDS_ENGINE_VERSION = 13;
/**
 * v14 (11/10/2026) : mode `bounds` à UNE borne — comparer une racine à un nombre
 * (√17 < 4,2) ou à une expression (2√3 < 3√2) par les carrés. Détectable sur le
 * modèle : `bounds` sans `lower` ou sans `upper`.
 */
export const ONE_BOUND_ENGINE_VERSION = 14;
/**
 * v15 (11/10/2026) : `stepwise` — depuis une équation de l'énoncé, la réponse ne
 * fait pas l'opération ET le calcul d'un bond : « x = 12 ÷ 3 » puis « x = 4 »
 * (équations à une étape, 5e). Détectable sur le modèle : `stepwise: true`.
 */
export const STEPWISE_ENGINE_VERSION = 15;
/**
 * v16 (11/10/2026) : `path` — corrigé écrit à la main, rejoué ligne à ligne par le juge
 * (porte, coup de pouce) quand le solveur ne sait pas le trouver seul (isoler une
 * variable dans une formule : v = d ÷ t → d = v × t). Détectable sur le modèle.
 */
export const PATH_ENGINE_VERSION = 16;
/**
 * v17 (11/10/2026) : fonctions réciproques arccos / arcsin / arctan, résultat en degrés
 * (collège : B = arccos(4 ÷ 5) ≈ 37°). Détectable sur le modèle (le mot apparaît).
 */
export const ARCTRIG_ENGINE_VERSION = 17;
/**
 * v18 (14/10/2026, refonte lot 4) : feuille jugée par le NOYAU (`kernel` du modèle) —
 * inéquations, équations à paramètre avec hypothèses déclarées. Chaque ligne est
 * vérifiée par le plugin algèbre (règles déclaratives, preuve exacte), le coup de
 * pouce vient de la recherche du noyau. Détectable sur le modèle : une feuille
 * `kernel` est en v18 ; les autres gardent leur version et leur ancien juge.
 */
export const KERNEL_ENGINE_VERSION = 18;
/**
 * v19 (14/10/2026, refonte « exp / ln ») : feuille du noyau qui écrit l'exponentielle,
 * le logarithme ou une puissance d'exposant littéral (« 1,5^n > 100 »). Le noyau les
 * juge avec le paquet `explog-core` (comparer les exposants, composer par ln ou par
 * exp) et les calculs du lot 3 bis : une app au moteur v18 ne saurait pas les juger
 * et masque ces feuilles. Détectable sur le modèle (les mots exp, ln, ou « ^ lettre »).
 */
export const KERNEL_EXPLOG_ENGINE_VERSION = 19;
/** la feuille du noyau écrit-elle exp, ln ou une puissance d'exposant littéral ? */
export function usesKernelExpLog(model: {
  kernel?: unknown;
  traps?: unknown[];
  choices?: unknown[];
}): boolean {
  const k = model.kernel as { start?: string; answer?: string; path?: string[] } | undefined;
  if (!k) return false;
  const texts = [
    k.start ?? '',
    k.answer ?? '',
    ...(k.path ?? []),
    ...((model.traps ?? []) as { line?: string }[]).map((t) => t?.line ?? ''),
    ...((model.choices ?? []) as { line?: string }[]).map((c) => c?.line ?? ''),
  ];
  return texts.some((t) => /\b(exp|ln)\b/.test(t) || /\^\s*\(?\s*[−-]?\s*[A-Za-zα-ωπ]/.test(t));
}
/**
 * v20 (14/10/2026, refonte « racines et trigonométrie ») : feuille du noyau qui écrit
 * une racine carrée ou cos / sin / tan, qui SIMPLIFIE une écriture (objectif
 * `simplify`), ou qui demande une réponse ARRONDIE (`rounding` du modèle : dernier pas
 * « AC ≈ 7,8 »). Le noyau les juge avec le paquet `radical-core` (élever au carré,
 * prendre la racine) et des calculs sur les racines nommés un par un (simplifier une
 * racine, valeur exacte, rendre le dénominateur rationnel). Une app au moteur v19
 * masque ces feuilles. Détectable sur le modèle.
 */
export const KERNEL_RADICAL_ENGINE_VERSION = 20;
/** la feuille du noyau écrit-elle une racine ou cos / sin / tan, simplifie-t-elle, arrondit-elle ? */
export function usesKernelRadical(model: {
  kernel?: unknown;
  rounding?: unknown;
  traps?: unknown[];
  choices?: unknown[];
}): boolean {
  const k = model.kernel as
    | { start?: string; answer?: string; path?: string[]; goal?: { kind?: string } }
    | undefined;
  if (!k) return false;
  if (model.rounding || k.goal?.kind === 'simplify') return true;
  const texts = [
    k.start ?? '',
    k.answer ?? '',
    ...(k.path ?? []),
    ...((model.traps ?? []) as { line?: string }[]).map((t) => t?.line ?? ''),
    ...((model.choices ?? []) as { line?: string }[]).map((c) => c?.line ?? ''),
  ];
  return texts.some((t) => /√|\b(cos|sin|tan)\b/.test(t));
}
/**
 * v21 (14/10/2026, refonte « angles ») : feuille du noyau qui cherche un ANGLE mesuré en
 * degrés (hypothèse `acute` ]0° ; 90°[ ou `angle` ]0° ; 180°[), ou qui écrit arccos,
 * arcsin, arctan. cos, sin, tan y lisent leur argument en degrés (« cos(Â) = 3 ÷ 5 »,
 * « Â = arccos(3 ÷ 5) ≈ 53,1° ») : une app au moteur v20 ne saurait pas les juger et
 * masque ces feuilles. Détectable sur le modèle.
 */
export const KERNEL_ANGLE_ENGINE_VERSION = 21;
/** la feuille du noyau cherche-t-elle un angle (hypothèse d'angle, ou arccos / arcsin / arctan) ? */
export function usesKernelAngle(model: {
  kernel?: unknown;
  traps?: unknown[];
  choices?: unknown[];
}): boolean {
  const k = model.kernel as
    | { start?: string; answer?: string; path?: string[]; hypotheses?: { kind?: string }[] }
    | undefined;
  if (!k) return false;
  if ((k.hypotheses ?? []).some((h) => h?.kind === 'acute' || h?.kind === 'angle')) return true;
  const texts = [
    k.start ?? '',
    k.answer ?? '',
    ...(k.path ?? []),
    ...((model.traps ?? []) as { line?: string }[]).map((t) => t?.line ?? ''),
    ...((model.choices ?? []) as { line?: string }[]).map((c) => c?.line ?? ''),
  ];
  return texts.some((t) => /\barc(cos|sin|tan)\b/.test(t));
}
/**
 * v22 (14/10/2026, refonte lot 5) : SYSTÈME d'équations linéaires jugé par le noyau
 * (`kernel.system` : 2 à 4 équations, 2 ou 3 inconnues, nombre de solutions annoncé).
 * Une ligne est juste si elle découle des équations (combinaison linéaire exacte) ;
 * les pas du cours : transformer une ligne, remplacer une inconnue isolée, combiner
 * deux lignes. Une app au moteur v21 masque ces feuilles. Détectable sur le modèle.
 */
export const KERNEL_SYSTEM_ENGINE_VERSION = 22;
/** la feuille du noyau est-elle un système d'équations ? */
export function usesKernelSystem(model: { kernel?: unknown }): boolean {
  const k = model.kernel as { system?: unknown } | undefined;
  return !!k?.system;
}
/**
 * v23 (15/10/2026, refonte lot 5.2) : équations et inéquations du SECOND DEGRÉ, de
 * produits et de quotients jugées par le noyau (`kernel.method = 'factor'`) : l'avancée
 * se mesure à la forme (tout d'un côté, factorisé, ensemble de solutions) ; les pas du
 * cours : factoriser, produit nul, tableau de signes, racine carrée (deux solutions),
 * discriminant et racines. Les réponses en intervalles s'écrivent avec des chaînes
 * (« −1 < x ≤ 2 ») et « ou » entre comparaisons (« x ≤ 2 ou x ≥ 3 ») : une app au
 * moteur v22 ne les lit pas et masque ces feuilles. Détectable sur le modèle.
 */
export const KERNEL_POLY_ENGINE_VERSION = 23;
/**
 * méthodes d'une feuille du noyau : `factor` (moteur v23) = second degré, produit, signe ;
 * `trig` (moteur v24) = équation trigonométrique (cos x = a, sin x = cos x…) ;
 * `test` (moteur v48) = seuil entier trouvé en testant des rangs (sans logarithme)
 */
export const KERNEL_METHODS = ['factor', 'trig', 'test'] as const;
/** deux comparaisons dans une ligne (chaîne), ou « ou » avec une comparaison */
const SET_LINE = /[<>≤≥][^=≈]*[<>≤≥]|[<>≤≥].*\bou\b|\bou\b.*[<>≤≥]/;
/** la feuille du noyau suit-elle la méthode « factor », ou écrit-elle un ensemble de solutions ? */
export function usesKernelPoly(model: {
  kernel?: unknown;
  traps?: unknown[];
  choices?: unknown[];
}): boolean {
  const k = model.kernel as
    | { start?: string; answer?: string; path?: string[]; method?: string }
    | undefined;
  if (!k) return false;
  if (k.method === 'factor') return true;
  // une équation trigonométrique (moteur v24) écrit aussi des intervalles : ce n'est pas « factor »
  if (k.method === 'trig' || k.method === 'test') return false;
  const texts = [
    k.start ?? '',
    k.answer ?? '',
    ...(k.path ?? []),
    ...((model.traps ?? []) as { line?: string }[]).map((t) => t?.line ?? ''),
    ...((model.choices ?? []) as { line?: string }[]).map((c) => c?.line ?? ''),
  ];
  return texts.some((t) => SET_LINE.test(t));
}
/**
 * v24 (15/10/2026, refonte lot 5.3) : ÉQUATIONS TRIGONOMÉTRIQUES jugées par le noyau
 * (`kernel.method = 'trig'`) : x est un réel (radians), les solutions s'écrivent en
 * familles (« x = π ÷ 3 + 2kπ ou x = −π ÷ 3 + 2kπ », k déclaré entier) ou sur un
 * intervalle (`kernel.domain` : « −π < x ≤ π »). Les pas du cours : valeur
 * remarquable, deux familles de solutions, lire sur le cercle, garder celles de
 * l'intervalle, diviser par cos(x). Une app au moteur v23 masque ces feuilles.
 */
export const KERNEL_TRIG_ENGINE_VERSION = 24;
/** la feuille du noyau est-elle une équation trigonométrique (méthode « trig ») ? */
export function usesKernelTrig(model: { kernel?: unknown }): boolean {
  const k = model.kernel as { method?: string; domain?: string; plugin?: string } | undefined;
  return (
    !!k &&
    k.plugin !== 'analysis' &&
    k.plugin !== 'variations' &&
    (k.method === 'trig' || !!k.domain)
  );
}
/**
 * v25 (15/10/2026, refonte lot 5.4) : GÉOMÉTRIE DÉDUCTIVE jugée par le noyau
 * (`kernel.plugin = 'geometry'`, objectif `prove`). Une ligne = une propriété
 * (« (MN) // (BC) », « I milieu de [AB] », « ABC rectangle en A ») ou une relation
 * entre longueurs (« AM ÷ AB = AN ÷ AC ») ; elle doit découler en UNE règle du paquet
 * geometry-core@1 (Thalès, Pythagore et leurs réciproques / contraposées, milieux,
 * parallélogrammes…) de l'énoncé et des lignes écrites. La figure de référence
 * (`kernel.figure`) dit si une ligne est vraie. Une app au moteur v24 masque ces feuilles.
 */
export const KERNEL_GEOMETRY_ENGINE_VERSION = 25;
/** la feuille du noyau est-elle une démonstration de géométrie ? */
export function usesKernelGeometry(model: { kernel?: unknown }): boolean {
  const k = model.kernel as { plugin?: string } | undefined;
  return !!k && k.plugin === 'geometry';
}
/**
 * v26 (15/10/2026, refonte lot 5.5) : HÉRÉDITÉ D'UNE RÉCURRENCE jugée par le noyau
 * (`kernel.induction`, objectif `prove`). L'énoncé : la définition de la suite
 * (« uₙ₊₁ = 3uₙ ») et l'hypothèse de récurrence P(n) (« uₙ = 3ⁿ », « 0 ≤ uₙ ≤ 2 »,
 * « 4ⁿ − 1 = 3k ») ; la réponse : P(n + 1). Une ligne doit être vraie sachant l'énoncé
 * et découler en un pas d'une ligne écrite : calculer, remplacer avec l'hypothèse de
 * récurrence ou la définition, transformer une relation. Une app au moteur v25 masque
 * ces feuilles. Détectable sur le modèle.
 */
export const KERNEL_RECURRENCE_ENGINE_VERSION = 26;
/** la feuille du noyau est-elle l'hérédité d'une récurrence ? */
export function usesKernelRecurrence(model: { kernel?: unknown }): boolean {
  const k = model.kernel as { induction?: unknown } | undefined;
  return !!k?.induction;
}
/**
 * v27 (15/10/2026, refonte lot 5.6) : DÉNOMBREMENT jugé par le noyau. « k parmi n » s'écrit
 * comme il se lit (mot `parmi`, coefficient binomial), les factorielles se calculent
 * exactement (100! ÷ 98! = 9900 sans calculer 100!) et se simplifient avec une inconnue
 * (n! ÷ (n − 2)! = n(n − 1)) ; paquet counting-core@1 (définition, 2 parmi n, symétrie,
 * Pascal, n! = n × (n − 1)!). Les SEUILS se concluent par le plus petit entier
 * (« n > ln(0,1) ÷ ln(0,5) » → « n ≥ 4 », n entier naturel). Une app au moteur v26 lit
 * « parmi » comme une grandeur et masque ces feuilles. Détectable sur le modèle.
 */
export const KERNEL_COUNT_ENGINE_VERSION = 27;
/**
 * v28 (15/10/2026, refonte lot 5.7) : TRACER UN PROGRAMME PYTHON (`kernel.plugin =
 * 'python'`, objectif `trace`). Le programme (`kernel.program`) est exécuté par
 * l'interpréteur sûr du noyau, en arithmétique exacte ; l'élève écrit l'état des
 * variables une unité à la fois (« i = 2 ; s = 3 » après un tour de boucle), les tests
 * (« u ≥ 100 ») et la valeur renvoyée (« pgcd(84, 36) = 12 »). Une app au moteur v27
 * masque ces feuilles. Détectable sur le modèle.
 */
export const KERNEL_PYTHON_ENGINE_VERSION = 28;
/** la feuille du noyau trace-t-elle un programme Python ? */
export function usesKernelPython(model: { kernel?: unknown }): boolean {
  const k = model.kernel as { plugin?: string } | undefined;
  return !!k && k.plugin === 'python';
}
/**
 * v29 (15/10/2026, refonte lot 5.7 bis) : PYTHON, la suite. Un calcul du programme
 * s'écrit (« n % 2 = 1 », 1 HORA) ; une boucle de 15 tours ou plus se trace en SAUTANT des
 * tours après deux tours écrits ; `None` s'écrit ; `random` avec des tirages imposés
 * (`program.draws`) ; objectif `complete` : écrire la ligne de code manquante (« ____ »),
 * jugée en exécutant le programme complété sur les tests (`program.checks`, `output`) ;
 * `program.display` : l'algorithme affiché tel que l'énoncé l'écrit (« Tant que … »).
 * Une app au moteur v28 masque ces feuilles. Détectable sur le modèle (une exécution du
 * programme pour la longueur des boucles).
 */
export const KERNEL_PYTHON_PLUS_ENGINE_VERSION = 29;
/**
 * v30 (15/10/2026, refonte lot 5.8) : CALCUL MATRICIEL (`kernel.plugin = 'matrix'`,
 * objectif `compute`). Les matrices de l'énoncé (`kernel.matrices`) et le calcul demandé
 * (`kernel.start` : « AB », « π₀P² », ou l'équation « AX = B ») ; chaque ligne est une
 * chaîne d'égalités évaluée exactement, chaque maillon un pas reconnu (remplacer, ligne
 * par colonne, coefficient par coefficient, inverse d'une 2 × 2…). Une app au moteur v29
 * masque ces feuilles. Détectable sur le modèle.
 */
export const KERNEL_MATRIX_ENGINE_VERSION = 30;
/** la feuille du noyau est-elle un calcul matriciel ? */
export function usesKernelMatrix(model: { kernel?: unknown }): boolean {
  const k = model.kernel as { plugin?: string } | undefined;
  return !!k && k.plugin === 'matrix';
}
/**
 * v31 (15/10/2026, refonte lot 5.9) : ANALYSE — dérivées et primitives
 * (`kernel.plugin = 'analysis'`, objectifs `differentiate` / `antiderivative`). La fonction
 * de l'énoncé (`kernel.start` : « f(x) = x × exp(x) »), éventuellement son intervalle
 * (`kernel.domain` : « x > 0 ») ; l'élève nomme les parties (u(x), v(x)), les dérive, applique
 * UNE règle (usuelles, produit, quotient, inverse, composée) puis remplace et simplifie ; une
 * primitive se vérifie en dérivant. Les fonctions se comparent par leurs valeurs en des
 * points de l'intervalle. Une app au moteur v30 masque ces feuilles.
 */
export const KERNEL_ANALYSIS_ENGINE_VERSION = 31;
/** la feuille du noyau est-elle une dérivée ou une primitive ? */
export function usesKernelAnalysis(model: { kernel?: unknown }): boolean {
  const k = model.kernel as { plugin?: string } | undefined;
  return !!k && k.plugin === 'analysis';
}
/**
 * lot 6.18 : un dénominateur de degré 4 ou plus au départ d'une feuille d'algèbre
 * (« 2(1 − x²) ÷ (1 + x²)² ≥ 0 ») — l'étude exacte d'une app v40 ne le tranchait pas
 * (porte refusée) et nommait le facteur développé (« Multiplier par (2x² + x^4 + 1) »).
 */
function kernelQuarticDenominator(model: { kernel?: unknown }): boolean {
  const k = model.kernel as { plugin?: string; start?: string; goal?: { target?: string } };
  if (k?.plugin !== 'algebra' || !k.start) return false;
  const p = parseStatement(k.start);
  if (!p.ok) return false;
  const x = k.goal?.target ?? 'x';
  const degree = (t: Term): number => {
    try {
      const c = univariateCoefs(toRatFn(t).n, x);
      return c ? c.length - 1 : 0;
    } catch {
      return 0;
    }
  };
  const quartic = (t: Term): boolean => {
    switch (t.k) {
      case 'div':
        return degree(t.b) >= 4 || quartic(t.a) || quartic(t.b);
      case 'add':
      case 'mul':
        return t.c.some(quartic);
      case 'pow':
      case 'binom':
        return quartic(t.a) || quartic(t.b);
      case 'fn':
        return quartic(t.a);
      default:
        return false;
    }
  };
  const s = p.s;
  return s.k === 'expr' ? quartic(s.e) : relationsIn(s).some((r) => quartic(r.l) || quartic(r.r));
}
/** dérivée seconde (« f′′(x) », lot 6.18, moteur v41) : une app plus ancienne lit « f′′ » f */
export function usesKernelSecondDerivative(model: { kernel?: unknown }): boolean {
  const k = model.kernel as { plugin?: string; goal?: { target?: string } } | undefined;
  return usesKernelAnalysis(model) && /′′|″|''/.test(k?.goal?.target ?? '');
}
/**
 * v32 (15/10/2026, refonte lot 6.1) : feuille « formules » jugée par le noyau (plugin
 * `formula`, objectif `evaluate`) — grandeurs données, formules de la SKILLCARD, une ou
 * plusieurs grandeurs à calculer. Chaque ligne est jugée VRAIE dans le monde de la
 * question (valeurs exactes) puis justifiée en un pas (formule, remplacer, calculer,
 * règle d'équation) ; une ligne juste qui saute des étapes reste sur la feuille, sans
 * HORA (D7) ; « ≈ » toujours proposé. Une app au moteur v31 masque ces feuilles.
 */
export const KERNEL_FORMULA_ENGINE_VERSION = 32;
export function usesKernelFormula(model: { kernel?: unknown }): boolean {
  return (model.kernel as { plugin?: string } | undefined)?.plugin === 'formula';
}
/**
 * v33 (16/10/2026, refonte lot 6.3) : écriture à DÉVELOPPER ou à FACTORISER jugée par le
 * noyau (plugin `algebra`, objectif `simplify`, clé `form`). L'avancée se mesure à la
 * forme de l'écriture : produits encore à développer, puis calculs (« expanded ») ; somme,
 * facteur ou carrés mis en évidence, produit (« factored »). Une app au moteur v32 masque
 * ces feuilles.
 */
export const KERNEL_FORM_ENGINE_VERSION = 33;
export function usesKernelForm(model: { kernel?: unknown }): boolean {
  return !!(model.kernel as { form?: string } | undefined)?.form;
}
/**
 * v34 (16/10/2026, refonte lot 6.4) : les feuilles en MODE SPÉCIAL de l'ancien moteur
 * passent au noyau — plugins `units` (objectif `convert`), `congruence` (`residue`),
 * `complex` (`calculate`) et `bounds` (`bound`). La lecture et le calcul de chaque mode
 * restent ceux de l'ancien moteur (unités, restes, calcul complexe exact, comparaisons) ;
 * les règles du noyau s'y ajoutent : une ligne JUSTE qui saute des étapes reste sur la
 * feuille, sans HORA, et « Tu tiens la réponse ! » si c'est la réponse (D7) ; les bouts
 * sont ceux de l'énoncé et du corrigé, sans les valeurs des pièges ni des choix (D6).
 * Le modèle garde la clé du mode (`units`, `congruence`, `complex`, `bounds`) ; `kernel`
 * en reprend le départ, la réponse et le corrigé. Une app au moteur v33 masque ces feuilles.
 */
export const KERNEL_SPECIAL_ENGINE_VERSION = 34;
export const KERNEL_SPECIAL_PLUGINS = ['units', 'congruence', 'complex', 'bounds'] as const;
export type KernelSpecialPlugin = (typeof KERNEL_SPECIAL_PLUGINS)[number];
/** l'objectif de chaque mode spécial */
export const KERNEL_SPECIAL_GOALS: Record<KernelSpecialPlugin, string> = {
  units: 'convert',
  congruence: 'residue',
  complex: 'calculate',
  bounds: 'bound',
};
export function kernelSpecialPlugin(model: { kernel?: unknown }): KernelSpecialPlugin | null {
  const p = (model.kernel as { plugin?: string } | undefined)?.plugin;
  return (KERNEL_SPECIAL_PLUGINS as readonly string[]).includes(p ?? '')
    ? (p as KernelSpecialPlugin)
    : null;
}
export function usesKernelSpecial(model: { kernel?: unknown }): boolean {
  return kernelSpecialPlugin(model) !== null;
}
/**
 * v35 (16/10/2026, refonte lot 5.10) : ARITHMÉTIQUE (`kernel.plugin = 'arith'`) —
 * l'algorithme d'Euclide (objectif `gcd` : « PGCD(1071 ; 462) » → « PGCD(1071 ; 462) = 21 »)
 * et la relation de Bézout (objectif `bezout` : « 17u + 5v = 1 » → « u = −2 ; v = 7 »).
 * Divisions euclidiennes, chaîne des PGCD, remontée de l'algorithme (isoler, remplacer,
 * regrouper), lecture du couple ; calcul exact sur les entiers. Une app au moteur v34
 * masque ces feuilles.
 */
export const KERNEL_ARITH_ENGINE_VERSION = 35;
export function usesKernelArith(model: { kernel?: unknown }): boolean {
  return (model.kernel as { plugin?: string } | undefined)?.plugin === 'arith';
}
/**
 * v36 (16/10/2026, refonte lot 6.8) : VÉRIFIER CHAQUE AFFIRMATION (`kernel.plugin =
 * 'claims'`, objectif `decide`). Un QCM « coche les affirmations vraies » : chaque choix
 * est une affirmation (`kernel.claims`, dans l'ordre des choix) que l'élève tranche par
 * un calcul exact ; la feuille la marque vraie ou fausse dès que ses membres sont
 * calculés. Une app au moteur v35 masque ces feuilles.
 */
export const KERNEL_CLAIMS_ENGINE_VERSION = 36;
export function usesKernelClaims(model: { kernel?: unknown }): boolean {
  return (model.kernel as { plugin?: string } | undefined)?.plugin === 'claims';
}
/**
 * v37 (16/10/2026, refonte lot 6.9) : la feuille d'affirmations sait les ANGLES ASSOCIÉS
 * (cos(π − x) = −cos(x)…, valeur lue sur le cercle : sin(5π ÷ 6) = 1 ÷ 2) et deux gestes
 * sur les racines (3 = √9, √40 = 2√10). Une feuille dont le corrigé s'en sert est en v37 ;
 * les autres feuilles d'affirmations restent en v36.
 */
export const KERNEL_CLAIMS_PLUS_ENGINE_VERSION = 37;
export function usesKernelClaimsPlus(model: { kernel?: unknown }): boolean {
  const k = model.kernel as
    | { plugin?: string; path?: string[]; claims?: { rels: string[] }[] }
    | undefined;
  if (k?.plugin !== 'claims') return false;
  const path = k.path ?? [];
  // valeur interdite (« 3 ÷ 0 ») : une app v36 refuserait l'affirmation
  return (
    pathUsesClaimsGestures(path) ||
    textsUseZeroDivision([...path, ...(k.claims ?? []).flatMap((c) => c.rels)])
  );
}
/**
 * v38 (16/10/2026, refonte lot 6.11) : un nombre ÉCRIT est lu exactement, avec tous ses
 * chiffres — avant, le lexique l'arrondissait à 6 décimales (« 0,00000035 » → « 0 ») et
 * un entier de 17 chiffres ou plus passait par un flottant (« 10²⁸ = 1000…0 » illisible).
 * Une feuille du noyau qui en écrit un est en v38 : une app v37 la lirait mal.
 */
export const LONG_DECIMAL_ENGINE_VERSION = 38;
/**
 * un nombre écrit avec 7 décimales ou plus, ou un entier de 17 chiffres ou plus (au-delà,
 * un flottant ne le garde pas exactement)
 */
export const LONG_DECIMAL_RE = /\d,\d{7,}|\d{17,}/;
/**
 * v39 (16/10/2026, refonte lot 6.13) : la feuille d'affirmations sait la FORME d'une
 * écriture (`kernel.claims[].form` : fraction irréductible, notation scientifique,
 * décomposition en facteurs premiers) et trois gestes (décomposer, regrouper en puissance,
 * puissance de 10) ; et une même expression à lettres testée pour plusieurs valeurs
 * (« 4x − 7 ≥ 5 » pour x = 3, x = 2… : la ligne « 4x − 7 = 4 × 3 − 7 » va à l'affirmation
 * x = 3 — une app v38 l'attribuait à la dernière). Une feuille d'affirmations qui s'en sert
 * est en v39.
 */
export const KERNEL_CLAIMS_FORM_ENGINE_VERSION = 39;
/**
 * v40 (16/10/2026, refonte lot 6.14) : affirmations de PROPRIÉTÉ (une écriture seule :
 * « 29 » nombre premier, « 6 ÷ 9 » fraction irréductible, « 4182 » divisible par 6, et leur
 * négation), le geste « Division euclidienne » (29 = 2 × 14 + 1) et l'élimination d'un QCM
 * à réponse unique (`kernel.single`). Une feuille d'affirmations qui s'en sert est en v40.
 */
export const KERNEL_CLAIMS_PROP_ENGINE_VERSION = 40;
/**
 * v41 (16/10/2026, refonte lot 6.16) : puissances et produits de lettres —
 *  - un exposant écrit après une lettre ou une parenthèse se lit (« x³ », « (2x)³ »,
 *    « a¹⁰ ») ; une app v40 déclarait la ligne illisible ;
 *  - « 2ab » se lit 2 × a × b quand a et b sont des lettres de la feuille ;
 *  - un cube face à une constante (« x³ = 27 », « x³ ≤ 8 », méthode `factor`) se résout
 *    par la fonction cube ;
 *  - développer compte les produits un par un (« (x − 1)(x + 1)(x − 2) » →
 *    « (x² − 1)(x − 2) » avance), (2x)³ → 2³ × x³ avance, et factoriser voit un facteur
 *    qui se factorise encore (« (x − 1)(x² − 5x + 6) ») ;
 *  - lot 6.17 : la feuille d'affirmations écrit une puissance en produit (« 0,2³ =
 *    0,2 × 0,2 × 0,2 ») et applique une règle des puissances à un seul endroit
 *    (« (5⁴ × 5²) ÷ 5³ = 5⁶ ÷ 5³ ») — une app v40 gardait ces lignes sans HORA ;
 *  - lot 6.18 : la dérivée seconde (« f′′(x) », deux étapes) ; le lexique lit « f′′(x) »
 *    (avant : « f(x) », les primes perdues) ; un dénominateur de degré 4 au départ
 *    (« ÷ (1 + x²)² », équation bicarrée dans l'étude exacte).
 * Une feuille qui s'en sert est en v41.
 */
export const KERNEL_POWER_ENGINE_VERSION = 41;
/**
 * v42 (16/10/2026, refonte lot 6.19) : le TABLEAU DE SIGNES dans la feuille. Une ligne
 * « ⊞{…} » (un seul bout, `kernel-signs.ts`) est un tableau que l'élève remplit : valeurs
 * de x, un signe par intervalle, 0 et double barre ; il est vérifié case par case
 * (2 HORA), puis la conclusion se LIT dans le tableau (1 HORA, au lieu des 3 HORA du
 * « Tableau de signes » fait de tête). Une feuille dont le corrigé dresse un tableau est
 * en v42 ; sur les autres feuilles « factor », le tableau reste facultatif.
 */
export const KERNEL_SIGN_TABLE_ENGINE_VERSION = 42;
/**
 * v42 étendu (lot 6.20) : l'ÉTUDE D'UNE FONCTION (plugin « variations ») — dériver f si
 * besoin, dresser le tableau de variations (signes de f′, flèches et valeurs de f), qui
 * tranche les choix du QCM (« f est décroissante sur… », « minimum de f »).
 */
export const KERNEL_VARIATIONS_ENGINE_VERSION = 42;
/** Feuille d'étude de fonction : tableau de variations (moteur v42 étendu). */
export function usesKernelVariations(model: { kernel?: unknown }): boolean {
  return (model.kernel as { plugin?: string } | undefined)?.plugin === 'variations';
}
/**
 * v42 étendu (lot 6.21) : REMPLIR UN TABLEAU (plugin « tables ») — statistiques (valeurs,
 * effectifs, effectifs cumulés → médiane, quartiles), proportionnalité (cases manquantes,
 * quotients → coefficient), double entrée (totaux) ; le tableau tranche les choix.
 */
export const KERNEL_TABLES_ENGINE_VERSION = 42;
/**
 * moteur v43 (refonte lot 6.41) : une écriture à mettre sous FORME CANONIQUE
 * (`kernel.form = 'canonical'`, « x² − 4x + 7 » → « (x − 2)² + 3 ») — mettre a en facteur,
 * compléter le carré, développer, calculer. Une app v42 la masque.
 */
export const KERNEL_CANONICAL_ENGINE_VERSION = 43;
export function usesKernelCanonicalForm(model: { kernel?: unknown }): boolean {
  return (model.kernel as { form?: string } | undefined)?.form === 'canonical';
}
/**
 * moteur v44 (refonte lot 6.42) : l'analyse en UN POINT (plugin « analysis ») —
 *  - l'équation de la TANGENTE (objectif `tangent`, `kernel.at` = l'abscisse a : « 0 »,
 *    « e ») : dériver f, calculer f(a) et f′(a), écrire y = f′(a)(x − a) + f(a), remplacer,
 *    réduire ;
 *  - une primitive avec une CONDITION (`kernel.condition` = « F(0) = 2 ») : F(x) = … + C,
 *    calculer F(a), en tirer C, remplacer ; l'énoncé peut ne donner que f′ (« f′(x) = 2x »,
 *    on cherche f).
 * La constante e s'y écrit « e ». Une app v43 masque ces feuilles.
 */
export const KERNEL_POINT_ENGINE_VERSION = 44;
export function usesKernelPoint(model: { kernel?: unknown }): boolean {
  const k = model.kernel as
    | { plugin?: string; goal?: { kind?: string }; at?: string; condition?: string }
    | undefined;
  return k?.plugin === 'analysis' && (k.goal?.kind === 'tangent' || !!k.condition);
}
/**
 * moteur v45 (refonte lot 6.43) : les règles de LN dans la feuille d'affirmations —
 * logarithme d'un produit, d'un quotient, d'une puissance, d'une racine, d'un inverse,
 * ln(1) = 0, ln(e) = 1, écrire un nombre comme une puissance (ln(8) = ln(2³)) ; ln(8),
 * ln(1 ÷ 4), ln(√2) ne sont plus des valeurs (ils se simplifient) ; la constante e s'écrit
 * « e » (ln(√e), e³√e). Une feuille d'affirmations dont le corrigé s'en sert, ou qui écrit
 * e, est en v45 : une app v44 garderait ces lignes sans HORA.
 */
export const KERNEL_CLAIMS_LN_ENGINE_VERSION = 45;
/**
 * moteur v46 (refonte lot 6.44) : une écriture à mettre en UNE SEULE FRACTION irréductible
 * (`kernel.form = 'fraction'`, « (n + 1) ÷ (n + 2) − n ÷ (n + 1) » → « 1 ÷ ((n + 1)(n + 2)) ») —
 * réduire au même dénominateur, développer le numérateur, réduire, simplifier. Une app v45
 * la masque.
 */
export const KERNEL_FRACTION_FORM_ENGINE_VERSION = 46;
export function usesKernelFractionForm(model: { kernel?: unknown }): boolean {
  return (model.kernel as { form?: string } | undefined)?.form === 'fraction';
}
/**
 * moteur v47 (refonte lot 6.47) : logarithmes et inconnue auxiliaire dans une feuille
 * d'algèbre — le logarithme décimal (10ˣ = c ⇔ x = log(c), log(x) ≥ 2 ⇔ x ≥ 10²), ln d'un
 * produit d'expressions (ln(x) + ln(x − 1) = ln(x(x − 1)), x > 1), une inconnue
 * auxiliaire déclarée (`kernel.auxiliary` : X = eˣ, X = ln(x)), des inconnues à
 * coefficients ln (2ˣ = 3ˣ⁻¹), une borne entière stricte (n > 2500 → n ≥ 2501), et des
 * libellés lisibles (« Diviser par 0,4 », « Diviser par e^(−6) ») pour une feuille qui
 * déclare `kernel.minEngine`. Une app v46 la masque.
 */
export const KERNEL_V47_ENGINE_VERSION = 47;
/**
 * v48 (refonte lot 6.48) : un SEUIL ENTIER trouvé en TESTANT des rangs (`kernel.method =
 * 'test'`, sans logarithme : 1re, 3e) — « 1000 × 0,8^11 ≈ 85,9 » teste n = 11, la conclusion
 * « n ≥ 11 » exige un rang qui ne convient pas et le suivant qui convient.
 */
export const KERNEL_TEST_ENGINE_VERSION = 48;
export function usesKernelTest(model: { kernel?: unknown }): boolean {
  return (model.kernel as { method?: string } | undefined)?.method === 'test';
}
/**
 * v49 (refonte lot 6.50) : le DOMAINE et la CONDITION d'une équation ou d'une inéquation
 * (`kernel.conditions`) — « ln(x) < 1 » : l'élève écrit d'abord « x > 0 » (ln(x) existe),
 * compose par exp (« x < e », juste avec la condition), puis tient compte de la condition
 * (« 0 < x < e ») ; « √(x + 3) = x − 3 » : « x − 3 ≥ 0 » (une racine est positive), élever
 * au carré, garder ce qui convient.
 */
export const KERNEL_CONDITION_ENGINE_VERSION = 49;
export function usesKernelCondition(model: { kernel?: unknown }): boolean {
  return !!(model.kernel as { conditions?: unknown[] } | undefined)?.conditions?.length;
}
/**
 * v50 (refonte lot 6.51) : des affirmations EN LETTRES dans la feuille d'affirmations —
 * une identité sans valeur proposée (« (x − 3)² = (3 − x)² », « 2(x + 4) − x = x + 8 »),
 * vraie si les deux membres sont égaux pour toutes les valeurs des lettres. On calcule
 * avec les lettres (développer, enlever les parenthèses, réduire, règles des puissances) ;
 * deux écritures développées et réduites différentes tranchent « fausse ».
 */
export const KERNEL_CLAIMS_LETTERS_ENGINE_VERSION = 50;
/**
 * v51 (refonte lot 6.54) : le PGCD et le PPCM PAR LES DÉCOMPOSITIONS en facteurs premiers
 * (plugin « arith », objectifs `gcd_primes` et `lcm`) : décomposer chaque nombre
 * (« 84 = 2² × 3 × 7 »), garder les facteurs communs au plus petit exposant (PGCD) ou tous
 * au plus grand (PPCM), puis calculer. Une app au moteur v50 masque ces feuilles.
 */
export const KERNEL_ARITH_PRIMES_ENGINE_VERSION = 51;
export function usesKernelArithPrimes(model: { kernel?: unknown }): boolean {
  const k = model.kernel as { plugin?: string; goal?: { kind?: string } } | undefined;
  return k?.plugin === 'arith' && (k.goal?.kind === 'gcd_primes' || k.goal?.kind === 'lcm');
}
/**
 * v52 (refonte lot 6.56) : deux ajouts additifs. (1) Calcul matriciel : un COEFFICIENT
 * s'écrit « (M²)₁₃ » — lu sur la matrice écrite (_Lire le coefficient_, 1 HORA) ou calculé
 * seul, la ligne i par la colonne j (_Ligne par colonne_, 2) : le nombre de chemins d'un
 * graphe. (2) Trace Python : une LISTE s'écrit comme valeur (« L = [1, 3, 5] », « , » suivie
 * d'une espace entre deux éléments). Une app au moteur v51 masque ces feuilles.
 */
export const KERNEL_LISTS_ENGINE_VERSION = 52;
/**
 * v53 (refonte lot 6.57) : la fin des fractions du groupe A, dans la feuille d'affirmations.
 * (1) Une fraction IRRÉDUCTIBLE par les facteurs premiers : décomposer son numérateur et son
 * dénominateur (« 16 ÷ 51 = 2⁴ ÷ (3 × 17) », _Décomposer en facteurs premiers_, 2 HORA) ; une
 * fraction écrite avec ses facteurs (« 2² ÷ (3 × 5) ») est irréductible quand ils n'ont aucun
 * facteur commun. (2) Les FRACTIONS EN LETTRES : les règles des puissances sur un quotient
 * (« a⁵ ÷ a⁸ = a^(5 − 8) = a^(−3) = 1 ÷ a³ », « a⁴ ÷ b⁴ = (a ÷ b)⁴ »), et « 1 ÷ a³ »,
 * « a⁴ ÷ b⁴ » sont des valeurs. Une app au moteur v52 masque ces feuilles.
 */
export const KERNEL_FRACTIONS_ENGINE_VERSION = 53;
/** une feuille d'affirmations qui se sert des fractions du moteur v53 */
export function usesKernelFractions(model: { kernel?: unknown }): boolean {
  const k = model.kernel as
    | {
        plugin?: string;
        path?: string[];
        claims?: { rels: string[]; form?: string; at?: Record<string, string> }[];
      }
    | undefined;
  if (k?.plugin !== 'claims') return false;
  const path = k.path ?? [];
  const claims = k.claims ?? [];
  return (
    claims.some((c) => isFactoredIrreducibleClaim(c)) ||
    pathUsesFractionPrimes(path) ||
    pathUsesLetterQuotients(path) ||
    textsUseMonomialQuotients([
      ...path,
      ...claims.filter((c) => !(c.at && Object.keys(c.at).length)).flatMap((c) => c.rels),
    ])
  );
}
/**
 * v54 (refonte lot 6.58) : les LIMITES (`kernel.plugin = 'limit'`, objectif `limit`) — une
 * expression, sa variable (`goal.target` : x, ou n pour une suite) et le point (`point` :
 * « +∞ », « −∞ », « 2 », « 2⁺ », « 0⁻ »). Une ligne : « expression → limite », une écriture
 * égale près du point (« x³ − 4x² + 1 = x³(1 − 4 ÷ x + 1 ÷ x³) »), ou un encadrement. Une app
 * au moteur v53 masque ces feuilles.
 */
export const KERNEL_LIMIT_ENGINE_VERSION = 54;
/** une feuille de limites (moteur v54) */
export function usesKernelLimit(model: { kernel?: unknown }): boolean {
  return (model.kernel as { plugin?: string } | undefined)?.plugin === 'limit';
}
/**
 * v55 (refonte lot 6.60) : les INTÉGRALES (`kernel.plugin = 'integral'`, objectif
 * `integrate`) — « I = ∫(a ; b) … dx » à calculer : intégration par parties, primitives,
 * linéarisation, éléments simples, Chasles, crochets « [F(x)](a ; b) », équation en I. Une
 * app au moteur v54 masque ces feuilles.
 */
export const KERNEL_INTEGRAL_ENGINE_VERSION = 55;
/** une feuille d'intégrales (moteur v55) */
export function usesKernelIntegral(model: { kernel?: unknown }): boolean {
  return (model.kernel as { plugin?: string } | undefined)?.plugin === 'integral';
}
/**
 * v56 (refonte lot 6.61) : la suite des LIMITES — arctan en radians (arctan(x) → π ÷ 2),
 * les deux théorèmes sur une composée (sin(1 ÷ n) ÷ (1 ÷ n) → 1, ln(x²) ÷ x² → 0),
 * uᵛ = e^(v × ln(u)) (forme 1^∞ : (1 + 1 ÷ n)ⁿ → e), sin(1 ÷ x) borné (x sin(1 ÷ x) → 0).
 * Une feuille de limites qui s'en sert (arctan écrit, ou corrigé qui ne se rejoue pas à
 * l'identique avec le moteur v54) est en v56 ; une app au moteur v55 la masque.
 */
export const KERNEL_LIMIT_PLUS_ENGINE_VERSION = 56;
/** une feuille de limites qui a besoin du moteur v56 */
export function usesKernelLimitPlus(model: { kernel?: unknown }): boolean {
  if (!usesKernelLimit(model)) return false;
  return klimNeedsV56(model as unknown as ScratchModel);
}
/**
 * v57 (refonte lot 6.62) : les AFFIRMATIONS DE LIMITES (`kernel.plugin = 'limclaims'`,
 * objectif `decide`) — « Quelles limites valent +∞ ? » : une limite affirmée par choix du
 * QCM (`lclaims` : expression, variable, point, limite affirmée), tranchée quand l'élève en
 * établit la vraie limite (juge des limites, au point de l'affirmation). Une app au moteur
 * v56 masque ces feuilles.
 */
export const KERNEL_LIMIT_CLAIMS_ENGINE_VERSION = 57;
/** une feuille d'affirmations de limites (moteur v57) */
export function usesKernelLimitClaims(model: { kernel?: unknown }): boolean {
  return (model.kernel as { plugin?: string } | undefined)?.plugin === 'limclaims';
}
/**
 * v58 (refonte lot 6.63) : les DÉVELOPPEMENTS LIMITÉS dans une feuille de limites — une
 * ligne « e^x = 1 + x + x² ÷ 2 + o(x²) » (le reste « o(x²) » est un bout), les gestes du
 * cours (DL de référence, composer, somme, produit, quotient, diviser par xᵏ), puis « → »
 * lu sur le développement ; réduire au même dénominateur (∞ − ∞). Une feuille de limites
 * dont le corrigé écrit un « o(…) » est en v58 ; une app au moteur v57 la masque.
 */
export const KERNEL_LIMIT_DL_ENGINE_VERSION = 58;
/** une feuille de limites qui écrit des développements limités (moteur v58) */
export function usesKernelLimitDL(model: { kernel?: unknown }): boolean {
  if (!usesKernelLimit(model)) return false;
  return usesDL(model as unknown as ScratchModel);
}
/**
 * v59 (refonte lot 6.64) : les SOMMES DE RIEMANN (`kernel.plugin = 'riemann'`, objectif
 * `riemann`) — une suite définie par une somme « Sₙ = Σ(k = 1 ; n) … » (ou un produit Π),
 * dont on cherche la limite : faire apparaître (1 ÷ n) × Σ f(k ÷ n), changer d'indice,
 * passer au logarithme, « Sₙ → ∫(a ; b) f(x) dx » (somme de Riemann), calculer l'intégrale
 * (comme une feuille d'intégrales), conclure. Une app au moteur v58 masque ces feuilles.
 */
export const KERNEL_RIEMANN_ENGINE_VERSION = 59;
/** une feuille de sommes de Riemann (moteur v59) */
export function usesKernelRiemann(model: { kernel?: unknown }): boolean {
  return (model.kernel as { plugin?: string } | undefined)?.plugin === 'riemann';
}
/**
 * v60 (refonte lot 6.65) : arctan, arcsin, arccos EN RADIANS dans une feuille d'intégrales,
 * de sommes de Riemann ou de dérivées / primitives — primitives 1 ÷ (1 + x²) → arctan(x),
 * 1 ÷ √(a² − x²) → arcsin(x ÷ a), u′ ÷ (1 + u²) à reconnaître, dérivées de arctan(u), et
 * leurs valeurs (arctan(1) = π ÷ 4). Le calcul du noyau garde les degrés pour les angles des
 * triangles ; une app au moteur v59 masque ces feuilles.
 */
export const KERNEL_ARC_ENGINE_VERSION = 60;
const ARC_TEXT = /\barc(?:tan|sin|cos)\s*\(/;
/** une feuille d'intégrales, de Riemann ou de dérivées qui écrit arctan, arcsin ou arccos */
export function usesKernelArc(model: {
  kernel?: unknown;
  givens?: unknown[];
  traps?: unknown[];
  choices?: unknown[];
}): boolean {
  const k = model.kernel as
    | {
        plugin?: string;
        start?: string;
        answer?: string;
        condition?: string;
        path?: string[];
      }
    | undefined;
  if (!k || !['integral', 'riemann', 'analysis'].includes(k.plugin ?? '')) return false;
  return [
    k.start ?? '',
    k.answer ?? '',
    k.condition ?? '',
    ...(k.path ?? []),
    ...(model.givens ?? []),
    ...(model.traps ?? []).map((t) => (t as { line?: string }).line ?? ''),
    ...(model.choices ?? []).map((c) => (c as { line?: string }).line ?? ''),
  ].some((t) => typeof t === 'string' && ARC_TEXT.test(t));
}
/**
 * v61 (refonte lot 6.66) : les ÉQUIVALENTS « ∼ » dans une feuille de limites — la réponse
 * « A ∼ B » (`kernel.answer`), une ligne « A ∼ B » vraie si A ÷ B tend vers 1 au point,
 * justifiée par un équivalent de référence, le premier terme d'un développement limité, le
 * terme dominant d'une somme ou un facteur remplacé par un équivalent. Une app au moteur
 * v60 masque ces feuilles.
 */
export const KERNEL_EQUIV_ENGINE_VERSION = 61;
/** une feuille de limites qui écrit « ∼ » (réponse, corrigé, énoncé, pièges, choix) */
export function usesKernelEquiv(model: {
  kernel?: unknown;
  givens?: unknown[];
  traps?: unknown[];
  choices?: unknown[];
}): boolean {
  const k = model.kernel as
    | { plugin?: string; start?: string; answer?: string; path?: string[] }
    | undefined;
  if (k?.plugin !== 'limit') return false;
  return [
    k.start ?? '',
    k.answer ?? '',
    ...(k.path ?? []),
    ...(model.givens ?? []),
    ...(model.traps ?? []).map((t) => (t as { line?: string }).line ?? ''),
    ...(model.choices ?? []).map((c) => (c as { line?: string }).line ?? ''),
  ].some((t) => typeof t === 'string' && /[∼~]/.test(t));
}
/**
 * v62 (refonte lot 6.67) : les ÉQUATIONS DIFFÉRENTIELLES (`kernel.plugin = 'ode'`, objectif
 * `solve_ode`) — une équation linéaire d'ordre 1 ou 2 en y, y′, y″ : la réécrire, trouver
 * toutes ses solutions, la solution qui vérifie des conditions initiales, ou une solution.
 * Une app au moteur v61 masque ces feuilles.
 */
export const KERNEL_ODE_ENGINE_VERSION = 62;
/** une feuille d'équation différentielle */
export function usesKernelOde(model: { kernel?: unknown }): boolean {
  return (model.kernel as { plugin?: string } | undefined)?.plugin === 'ode';
}
/**
 * v63 (refonte lot 6.68) : une équation différentielle en t (`kernel.ode.variable`), ou dont
 * la question demande la VALEUR de la solution en un point (réponse « f(0) = … ») ; une
 * primitive dont l'énoncé ne donne que la dérivée (« v′(x) = sin(x) », sans condition).
 * Une app au moteur v62 masque ces feuilles.
 */
export const KERNEL_ODE_PLUS_ENGINE_VERSION = 63;
export function usesKernelOdePlus(model: { kernel?: unknown }): boolean {
  const k = model.kernel as
    | {
        plugin?: string;
        start?: string;
        answer?: string;
        condition?: string;
        goal?: { kind?: string; target?: string };
        ode?: { variable?: string };
      }
    | undefined;
  if (!k) return false;
  if (k.plugin === 'ode') {
    if (k.ode?.variable && k.ode.variable !== 'x') return true;
    const fn = (k.goal?.target ?? 'y').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(`^\\s*${fn}\\(`).test(k.answer ?? '');
  }
  // une primitive qui part de « v′(x) = … » sans condition
  return (
    k.plugin === 'analysis' &&
    k.goal?.kind === 'antiderivative' &&
    !k.condition &&
    /^[^=]*[′']\s*\(/.test(k.start ?? '')
  );
}
/**
 * v64 (refonte lot 6.69) : les DÉRIVÉES PARTIELLES (`kernel.plugin = 'partial'`, objectif
 * `partial`) — dérivées premières et secondes, gradient, laplacien, valeur en un point,
 * règle de la chaîne le long d'une courbe. Une app au moteur v63 masque ces feuilles.
 */
export const KERNEL_PARTIAL_ENGINE_VERSION = 64;
export function usesKernelPartial(model: { kernel?: unknown }): boolean {
  return (model.kernel as { plugin?: string } | undefined)?.plugin === 'partial';
}
/** une feuille de calcul matriciel qui écrit un coefficient « (M²)₁₃ » (moteur v52) */
export function usesKernelMatrixEntry(model: {
  kernel?: unknown;
  traps?: unknown[];
  choices?: unknown[];
}): boolean {
  const k = model.kernel as
    | {
        plugin?: string;
        start?: string;
        answer?: string;
        path?: string[];
        goal?: { target?: string };
      }
    | undefined;
  if (k?.plugin !== 'matrix') return false;
  const texts = [
    k.start ?? '',
    k.answer ?? '',
    k.goal?.target ?? '',
    ...(k.path ?? []),
    ...((model.traps ?? []) as { line?: string }[]).map((t) => t.line ?? ''),
    ...((model.choices ?? []) as { line?: string }[]).map((c) => c.line ?? ''),
  ];
  return texts.some((t) => ENTRY_TEXT_RE.test(t));
}
/** une trace Python qui écrit une liste « L = [1, 3, 5] » (moteur v52) */
export function usesKernelPythonLists(model: {
  kernel?: unknown;
  traps?: unknown[];
  choices?: unknown[];
}): boolean {
  const k = model.kernel as
    | { plugin?: string; answer?: string; path?: string[]; program?: { checks?: string[] } }
    | undefined;
  if (k?.plugin !== 'python') return false;
  const texts = [
    k.answer ?? '',
    ...(k.path ?? []),
    ...(k.program?.checks ?? []),
    ...((model.traps ?? []) as { line?: string }[]).map((t) => t.line ?? ''),
    ...((model.choices ?? []) as { line?: string }[]).map((c) => c.line ?? ''),
  ];
  // une liste comme VALEUR (« L = [1, 3] ») ; un appel « compte([3, 8], 5) » (v28) n'en est pas une
  return texts.some((t) => /=\s*\[/.test(t));
}
export function usesKernelClaimsLetters(model: { kernel?: unknown }): boolean {
  const k = model.kernel as
    | { plugin?: string; claims?: { rels: string[]; at?: Record<string, string> }[] }
    | undefined;
  if (k?.plugin !== 'claims') return false;
  const claims = k.claims ?? [];
  const eConst = !claims.some((c) => c.at && 'e' in c.at);
  return claims.some(
    (c) =>
      !(c.at && Object.keys(c.at).length) &&
      !isPropertyClaim(c) &&
      c.rels.some((t) => {
        const p = parseStatement(t);
        if (!p.ok) return false;
        return [...statementSymbols(p.s)].some((n) => n !== 'π' && !(eConst && n === 'e'));
      }),
  );
}
export function usesKernelV47(model: { kernel?: unknown; choices?: { line?: string }[] }): boolean {
  const k = model.kernel as
    | {
        plugin?: string;
        goal?: { target?: string };
        start?: string;
        answer?: string;
        path?: string[];
        auxiliary?: unknown;
        minEngine?: number;
      }
    | undefined;
  if (!k || k.plugin !== 'algebra') return false;
  if (k.auxiliary || (k.minEngine ?? 0) >= KERNEL_V47_ENGINE_VERSION) return true;
  const x = k.goal?.target ?? 'x';
  const texts = [
    k.start ?? '',
    k.answer ?? '',
    ...(k.path ?? []),
    ...(model.choices ?? []).map((c) => c.line ?? ''),
  ];
  return texts.some((t) => /\blog\(/.test(t) || lnSumOf(t, x));
}
/** une somme de deux logarithmes (ou plus) de l'inconnue : « ln(x) + ln(x − 1) » */
function lnSumOf(text: string, x: string): boolean {
  const p = parseStatement(text);
  if (!p.ok) return false;
  const lnOfX = (t: Term): boolean =>
    (t.k === 'fn' && t.f === 'ln' && termHas(t.a, x)) ||
    (t.k === 'mul' && t.c.some((c) => c.k === 'fn' && c.f === 'ln' && termHas(c.a, x)));
  const walk = (t: Term): boolean => {
    if (t.k === 'add' && t.c.filter(lnOfX).length >= 2) return true;
    switch (t.k) {
      case 'add':
      case 'mul':
        return t.c.some(walk);
      case 'div':
      case 'pow':
      case 'binom':
        return walk(t.a) || walk(t.b);
      case 'fn':
        return walk(t.a);
      default:
        return false;
    }
  };
  return relationsIn(p.s).some((r) => walk(r.l) || walk(r.r));
}
function termHas(t: Term, x: string): boolean {
  switch (t.k) {
    case 'sym':
      return t.n === x;
    case 'num':
      return false;
    case 'add':
    case 'mul':
      return t.c.some((c) => termHas(c, x));
    case 'div':
    case 'pow':
    case 'binom':
      return termHas(t.a, x) || termHas(t.b, x);
    case 'fn':
      return termHas(t.a, x);
  }
}
export function usesKernelClaimsLn(model: { kernel?: unknown }): boolean {
  const k = model.kernel as
    | {
        plugin?: string;
        path?: string[];
        claims?: { rels: string[]; at?: Record<string, string> }[];
      }
    | undefined;
  if (k?.plugin !== 'claims') return false;
  const path = k.path ?? [];
  const eConst = !(k.claims ?? []).some((c) => c.at && 'e' in c.at);
  const texts = [...path, ...(k.claims ?? []).flatMap((c) => c.rels)];
  return (
    pathUsesClaimsLn(path) ||
    (eConst && texts.some((t) => E_CONSTANT_RE.test(t))) ||
    // ln d'un nombre négatif ou nul : une app v44 ne sait pas trancher l'affirmation
    texts.some((t) => /ln\(\s*(−|-|0\s*\))/.test(t))
  );
}
/** Feuille « tableau à remplir » (moteur v42 étendu). */
export function usesKernelTables(model: { kernel?: unknown }): boolean {
  return (model.kernel as { plugin?: string } | undefined)?.plugin === 'tables';
}
/** Le corrigé de la feuille dresse un tableau de signes (moteur v42). */
export function usesKernelSignTable(model: { kernel?: unknown }): boolean {
  const k = model.kernel as { path?: unknown } | undefined;
  return Array.isArray(k?.path) && k.path.some((t) => typeof t === 'string' && t.startsWith('⊞'));
}
/** feuille d'affirmations dont le corrigé écrit une puissance en produit, ou applique une
 * règle des puissances à un seul endroit (« (5⁴ × 5²) ÷ 5³ = 5⁶ ÷ 5³ », lot 6.17) */
function usesClaimsPower41(model: { kernel?: unknown }): boolean {
  const k = model.kernel as { plugin?: string; path?: string[] } | undefined;
  return k?.plugin === 'claims' && pathUsesClaimsPower41(k.path ?? []);
}
/** une ligne « cube face à une constante » (« x³ = 27 », « x^3 ≤ 8 ») */
function kernelCubeLine(line: string, x: string): boolean {
  const p = parseStatement(line);
  return p.ok && p.s.k === 'rel' && relForm(p.s, x) === 'cube';
}
export function usesKernelClaimsProp(model: { kernel?: unknown }): boolean {
  const k = model.kernel as
    | { plugin?: string; path?: string[]; single?: boolean; claims?: { rels: string[] }[] }
    | undefined;
  if (k?.plugin !== 'claims') return false;
  return (
    !!k.single ||
    (k.claims ?? []).some((c) => isPropertyClaim(c)) ||
    pathUsesClaimsEuclid(k.path ?? [])
  );
}
export function usesKernelClaimsForm(model: { kernel?: unknown }): boolean {
  const k = model.kernel as
    | {
        plugin?: string;
        path?: string[];
        claims?: { form?: string; rels?: string[]; at?: Record<string, string> }[];
      }
    | undefined;
  if (k?.plugin !== 'claims') return false;
  return (
    (k.claims ?? []).some((c) => !!c.form) ||
    pathUsesClaimsFormGestures(k.path ?? []) ||
    claimsShareLetteredSide(k.claims ?? [])
  );
}
/** deux affirmations à valeurs proposées (`at`) qui écrivent un même membre à lettres */
function claimsShareLetteredSide(
  claims: readonly { rels?: string[]; at?: Record<string, string> }[],
): boolean {
  const seen = new Map<string, number>();
  let shared = false;
  claims.forEach((c, i) => {
    if (!c.at || !Object.keys(c.at).length) return;
    for (const rel of c.rels ?? [])
      for (const side of rel.split(/[=<>≤≥]/)) {
        const t = side.replace(/\s+/g, '');
        if (!Object.keys(c.at!).some((n) => t.includes(n))) continue;
        const j = seen.get(t);
        if (j !== undefined && j !== i) shared = true;
        else seen.set(t, i);
      }
  });
  return shared;
}
/**
 * v38 aussi (refonte lot 6.12) : une écriture à développer ou à simplifier avec des
 * exponentielles (`kernel.form`) se juge règle par règle — « e^(3x) × e^(−x + 2) » →
 * « e^(3x − x + 2) » avance (avant : « ça ne t’avance pas », seul le bond jusqu’à
 * « e^(2x + 2) » comptait). Le corrigé d'une telle feuille s'en sert : une app v37 la masque.
 */
export const KERNEL_EXP_FORM_ENGINE_VERSION = 38;
export function usesKernelExpForm(model: { kernel?: unknown }): boolean {
  const k = model.kernel as
    | { form?: string; start?: string; answer?: string; path?: string[] }
    | undefined;
  if (!k?.form) return false;
  return [k.start ?? '', k.answer ?? '', ...(k.path ?? [])].some((t) => /e\^|exp\(/.test(t));
}
export function usesLongDecimals(model: { kernel?: unknown }): boolean {
  if (!model.kernel) return false;
  const k = model.kernel as { claims?: { rels?: string[]; at?: Record<string, string> }[] };
  // les LIGNES seulement (jamais les nombres JSON d'une figure : 0.3333333333333333)
  const texts = [
    ...modelLineTexts(model as Record<string, unknown>),
    ...(k.claims ?? []).flatMap((c) => [...(c.rels ?? []), ...Object.values(c.at ?? {})]),
  ];
  return texts.some((t) => LONG_DECIMAL_RE.test(t));
}
/** textes de lignes d'un modèle (feuille du noyau ou à formules), jamais les libellés */
function modelLineTexts(model: Record<string, unknown>): string[] {
  const m = model as {
    kernel?: { start?: string; answer?: string; path?: string[]; givens?: string[] };
    symbols?: Record<string, { exact?: string[] }>;
    givens?: string[];
    admitted?: string[];
    answers?: string[];
    preds?: { line?: string }[];
    traps?: { line?: string }[];
    checks?: { line?: string }[];
    choices?: { line?: string }[];
  };
  const k = m.kernel;
  return [
    k?.start ?? '',
    k?.answer ?? '',
    ...(k?.path ?? []),
    ...(k?.givens ?? []),
    ...Object.keys(m.symbols ?? {}),
    ...Object.values(m.symbols ?? {}).flatMap((s) => s?.exact ?? []),
    ...(m.givens ?? []),
    ...(m.admitted ?? []),
    ...(m.answers ?? []),
    ...(m.preds ?? []).map((p) => p?.line ?? ''),
    ...(m.traps ?? []).map((t) => t?.line ?? ''),
    ...(m.checks ?? []).map((c) => c?.line ?? ''),
    ...(m.choices ?? []).map((c) => c?.line ?? ''),
  ];
}
/** une ligne du modèle écrit-elle « k parmi n » ? (une app v26 ne le lirait pas) */
export function usesParmi(model: Record<string, unknown>): boolean {
  return modelLineTexts(model).some((t) => /(^|[^A-Za-zÀ-ÿ])parmi([^A-Za-zÀ-ÿ]|$)/.test(t));
}
/** la feuille du noyau dénombre-t-elle (parmi, factorielle) ou conclut-elle par un entier ? */
export function usesKernelCount(model: { kernel?: unknown }): boolean {
  const k = model.kernel as
    | {
        start?: string;
        answer?: string;
        path?: string[];
        goal?: { target?: string };
        hypotheses?: { kind?: string; of?: string }[];
      }
    | undefined;
  if (!k) return false;
  const texts = [k.start ?? '', k.answer ?? '', ...(k.path ?? [])];
  if (texts.some((t) => t.includes('!') || /(^|[^A-Za-zÀ-ÿ])parmi([^A-Za-zÀ-ÿ]|$)/.test(t)))
    return true;
  return integerBoundAnswer(k);
}
/**
 * Réponse « n ≥ 4 » (inégalité entre l'inconnue entière et un entier) : le seuil se conclut
 * par le plus petit (ou le plus grand) entier (moteur v27).
 */
export function integerBoundAnswer(k: {
  answer?: string;
  goal?: { target?: string };
  hypotheses?: { kind?: string; of?: string }[];
}): boolean {
  const x = k.goal?.target;
  if (!x || !k.answer) return false;
  const whole = (k.hypotheses ?? []).some(
    (h) => (h?.kind === 'natural' || h?.kind === 'integer') && h?.of === x,
  );
  if (!whole) return false;
  const esc = x.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`^\\s*${esc}\\s*[<>≤≥]\\s*[−-]?\\d+\\s*$`).test(k.answer);
}
/** la feuille écrit-elle une factorielle ou un cardinal ? (lignes et noms seulement) */
function usesCounting(model: Record<string, unknown>): boolean {
  const m = model as {
    symbols?: Record<string, { exact?: string[] }>;
    givens?: string[];
    admitted?: string[];
    answers?: string[];
    preds?: { line?: string }[];
    traps?: { line: string }[];
    checks?: { line: string }[];
    choices?: { line?: string }[];
  };
  const texts = [
    ...Object.keys(m.symbols ?? {}),
    ...Object.values(m.symbols ?? {}).flatMap((s) => s.exact ?? []),
    ...(m.givens ?? []),
    ...(m.admitted ?? []),
    ...(m.answers ?? []),
    ...(m.preds ?? []).map((p) => p.line ?? ''),
    ...(m.traps ?? []).map((t) => t.line),
    ...(m.checks ?? []).map((c) => c.line),
    ...(m.choices ?? []).map((c) => c.line ?? ''),
  ];
  return texts.some((t) => t.includes('!') || /(^|[^A-Za-z])card\(/.test(t));
}
/**
 * v4 (10/10/2026) : grandeurs et unités (« 3,5 m³ = 3500 L », mode `units`).
 * Une feuille sans unités garde sa version (2 ou 3).
 */
export const UNITS_ENGINE_VERSION = 4;

const name = z.string().min(1).max(40);
const lineText = z.string().min(1).max(400);

export const SymbolSpecSchema = z.object({
  /** grandeur donnée par l'énoncé : sa valeur est connue dès le départ */
  given: z.boolean().optional(),
  /**
   * grandeur de la SKILLCARD (V₀, V₁, k…) : le panneau la range dans les
   * « outils de la carte », un groupe replié — l'énoncé reste léger.
   */
  tool: z.boolean().optional(),
  /** sens de la lettre, affiché dans les notations de la feuille : « taux d'évolution » */
  meaning: z.string().min(1).max(80).optional(),
  value: z.number().optional(),
  /** valeur(s) que la grandeur prend dans l'exercice (plusieurs : x₁, x₂, solutions…) */
  values: z.array(z.number()).max(8).optional(),
  /** length = positive (autorise la racine carrée) ; free = variable d'identité ; int = rang */
  kind: z.enum(['length', 'free', 'int']).optional(),
  /**
   * valeurs EXACTES écrites en expression (moteur v2) : « ln(5) ÷ 2 », « √2 ÷ 2 ».
   * Le moteur en calcule `value` (grandeur donnée) ou `values` : le générateur
   * n'écrit jamais d'arrondi.
   */
  exact: z.array(lineText).max(8).optional(),
});
export type SymbolSpec = z.infer<typeof SymbolSpecSchema>;

export const TrapSpecSchema = z.object({
  line: lineText,
  title: z.string().min(1).max(120),
  why: z.string().min(1).max(600),
  /** erreur type du catalogue `misconceptions` (piège venu d'une règle de SKILLCARD) */
  misconception_id: z.string().min(1).max(80).optional(),
});
export type TrapSpec = z.infer<typeof TrapSpecSchema>;

// ── Outils (catalogue fermé) ────────────────────────────────────────────────

export const PythagorePredSchema = z.object({
  skill: z.literal('pythagore'),
  /** sommet de l'angle droit */
  right: name,
  /** hypoténuse */
  hyp: name,
  /** les deux autres côtés */
  sides: z.tuple([name, name]),
});
export const BaissePredSchema = z.object({
  skill: z.literal('baisse'),
  base: z.union([z.number(), name]),
  taux: z.number().positive().max(100),
  remise: name,
  nouveau: name,
});
export const ProduitNulPredSchema = z.object({ skill: z.literal('produitNul') });
export const FacteurCommunPredSchema = z.object({ skill: z.literal('facteurCommun') });
export const SecondDegrePredSchema = z.object({
  skill: z.literal('secondDegre'),
  a: name,
  b: name,
  c: name,
  delta: name,
  roots: z.tuple([name, name]),
  /** solution double quand Δ = 0 (moteur v12) : x₀ = −b ÷ (2a) */
  double: name.optional(),
});
export const DifferenceCarresPredSchema = z.object({ skill: z.literal('differenceCarres') });
/** a² ± 2ab + b² = (a ± b)² : factoriser un carré parfait */
export const CarreParfaitPredSchema = z.object({ skill: z.literal('carreParfait') });
export const FormulePredSchema = z.object({
  skill: z.literal('formule'),
  /** la formule, écrite avec les grandeurs du modèle : « P = 2 × (L + l) » */
  line: lineText,
  label: z.string().max(60).optional(),
  hora: z.number().int().min(1).max(6).optional(),
  why: z.string().max(600).optional(),
  hint: z.string().max(600).optional(),
  /** règle de SKILLCARD dont la formule est tirée : « <card_id>:<rule_key> » (traçabilité) */
  rule: z.string().max(80).optional(),
});
export const RecurrencePredSchema = z.object({
  skill: z.literal('recurrence'),
  /** P(n) : « uₙ = 2ⁿ − 1 » */
  hyp: lineText,
  /** P(0) : « u₀ = 2⁰ − 1 » */
  init: lineText,
  /** P(n+1) : « uₙ₊₁ = 2ⁿ⁺¹ − 1 » */
  next: lineText,
  /** instances utiles une fois la récurrence prouvée : « u₁₀ = 2¹⁰ − 1 » */
  inst: z.array(lineText).max(4).optional(),
});

export const PredSchema = z.discriminatedUnion('skill', [
  PythagorePredSchema,
  BaissePredSchema,
  ProduitNulPredSchema,
  FacteurCommunPredSchema,
  SecondDegrePredSchema,
  DifferenceCarresPredSchema,
  CarreParfaitPredSchema,
  FormulePredSchema,
  RecurrencePredSchema,
]);
export type Pred = z.infer<typeof PredSchema>;
export type SkillId = Pred['skill'];
export type PythagorePred = z.infer<typeof PythagorePredSchema>;
export type BaissePred = z.infer<typeof BaissePredSchema>;
export type SecondDegrePred = z.infer<typeof SecondDegrePredSchema>;
export type FormulePred = z.infer<typeof FormulePredSchema>;
export type RecurrencePred = z.infer<typeof RecurrencePredSchema>;

/** Les outils connus du moteur, dans l'ordre où ils sont consultés. */
export const SKILL_IDS: readonly SkillId[] = [
  'pythagore',
  'baisse',
  'produitNul',
  'facteurCommun',
  'secondDegre',
  'differenceCarres',
  'carreParfait',
  'formule',
  'recurrence',
];

// ── Choix du QCM, échantillons ──────────────────────────────────────────────

/** Un choix du QCM de la question, relié au modèle (« Tu tiens la réponse », contrôle des distracteurs). */
export const ChoiceSpecSchema = z.object({
  /** bloc correction (bonne réponse) ou distracteur */
  id: z.string().max(64).optional(),
  label: z.string().min(1).max(200),
  correct: z.boolean(),
  /** réponse sous forme d'égalité (« A = (x + 3)(x − 5) ») */
  line: lineText.optional(),
  /** réponse numérique (valeur de la grandeur cherchée) ; ni line ni value : choix non vérifiable */
  value: z.number().optional(),
  /** réponse numérique EXACTE écrite en expression (moteur v2) : « ln(5) ÷ 2 » → `value` calculée */
  exact: lineText.optional(),
});
export type ChoiceSpec = z.infer<typeof ChoiceSpecSchema>;

/**
 * Échantillons pour juger une égalité « pour tout x » (identités, récurrence) :
 * le moteur calcule lui-même les environnements (`envs`) à partir des valeurs
 * des variables libres et des définitions. Le générateur ne calcule rien à la main.
 */
export const SamplesSpecSchema = z.object({
  /** valeurs des variables libres : { x: [−3, −1, 0, 2, 3.5] } (produit cartésien, 16 environnements au plus) */
  vars: z.record(name, z.array(z.number()).min(1).max(16)),
  /** définitions « S = expression », évaluées dans l'ordre : « A = (x + 3)(2x − 1) − (x + 3)(x + 4) » */
  defs: z.array(lineText).max(8).optional(),
});
export type SamplesSpec = z.infer<typeof SamplesSpecSchema>;

/**
 * Congruences (moteur v3) : la question se joue modulo `mod`. La cible est une
 * expression sans inconnue (« 2¹⁰⁰ », « 5 × 9 ») ou l'inconnue (« x ») ; le but
 * est de l'écrire congrue à son reste entre 0 et mod − 1. Le corrigé (`path`) est
 * rejoué ligne à ligne par le juge à la validation.
 */
export const CongruenceSpecSchema = z.object({
  mod: z.number().int().min(2).max(100000),
  target: lineText,
  /** reste attendu (calculé si la cible n'a pas d'inconnue) */
  residue: z.number().int().min(0).optional(),
  path: z.array(lineText).min(1).max(12),
});
export type CongruenceSpec = z.infer<typeof CongruenceSpecSchema>;

/**
 * Nombres complexes (moteur v11) : la question calcule avec i (« (1 + i)⁸ »).
 * Chaque ligne est une chaîne de « = » jugée en calcul complexe exact (i² = −1),
 * chaque maillon justifié en un pas ; le but est une forme algébrique a + bi.
 */
export const ComplexSpecSchema = z.object({
  /** l'expression à calculer, avec i : « (1 + i)^8 » */
  target: lineText,
  /** le corrigé, ligne à ligne (rejoué par la porte) */
  path: z.array(lineText).min(1).max(12),
  /** forme de la réponse : algébrique a + bi (défaut) ou exponentielle r e^(iθ) */
  form: z.enum(['algebraic', 'exponential']).optional(),
  /** équation en z : l'inconnue (la cible est alors l'équation) */
  unknown: name.optional(),
  /** ses solutions, écrites avec i */
  solutions: z.array(lineText).max(6).optional(),
  /** grandeurs définies par une formule : { Δ: '2^2 − 4 × 1 × 5' } */
  defs: z.record(name, lineText).optional(),
});
export type ComplexSpec = z.infer<typeof ComplexSpecSchema>;

/**
 * Grandeurs et unités (moteur v4) : la question convertit ou calcule une
 * grandeur (`target`, ex. « 3,5 m³ », « 4 m × 250 cm », « 120 km ÷ 1,5 h »)
 * et attend le résultat dans l'unité `unit` (« L », « m² », « km/h »,
 * « h min » pour une durée en heures et minutes). Les valeurs des choix du QCM
 * sont exprimées dans `valueUnit` (défaut : la dernière unité de `unit`).
 * Le corrigé (`path`) est rejoué ligne à ligne par le juge à la validation.
 */
export const UnitsSpecSchema = z.object({
  target: lineText,
  unit: z.string().min(1).max(20),
  valueUnit: z.string().min(1).max(12).optional(),
  path: z.array(lineText).min(1).max(12),
});
export type UnitsSpec = z.infer<typeof UnitsSpecSchema>;

/**
 * Raisonnement géométrique (moteur v4) : démontrer une proposition (`goal`,
 * ex. « (d₁) // (d₃) », « J milieu de [AC] ») à partir des `givens`, une
 * propriété du cours par ligne. Le corrigé (`path`) est rejoué par le juge.
 * Les choix du QCM portent la proposition dans `line`.
 */
export const GeometrySpecSchema = z.object({
  goal: lineText,
  path: z.array(lineText).min(1).max(12),
});
export type GeometrySpec = z.infer<typeof GeometrySpecSchema>;
/**
 * Encadrer une racine carrée (moteur v13) : `expr` (« √40 ») entre `lower` et
 * `upper` ; le corrigé (`path`) est rejoué par le juge. Choix du QCM : sans valeur.
 */
export const BoundsSpecSchema = z
  .object({
    expr: lineText,
    lower: z.string().min(1).max(20).optional(),
    upper: z.string().min(1).max(20).optional(),
    path: z.array(lineText).min(1).max(12),
  })
  .refine((b) => b.lower !== undefined || b.upper !== undefined, {
    message: 'au moins une borne',
  });
export type BoundsSpec = z.infer<typeof BoundsSpecSchema>;

/**
 * Feuille jugée par le noyau (moteur v18). Hypothèses : une condition de signe sur
 * un terme (« m − 1 » positif), affichée en tête de feuille (« m > 1 »).
 */
export const KERNEL_HYPOTHESIS_KINDS = [
  'positive',
  'negative',
  'nonzero',
  'nonnegative',
  'integer',
  'natural',
  // moteur v21 : un angle mesuré en degrés, aigu (]0° ; 90°[) ou d'un triangle (]0° ; 180°[)
  'acute',
  'angle',
] as const;
export type KernelHypothesisKind = (typeof KERNEL_HYPOTHESIS_KINDS)[number];
export const KernelHypothesisSchema = z.object({
  kind: z.enum(KERNEL_HYPOTHESIS_KINDS),
  /** le terme concerné, écrit avec les grandeurs du modèle : « m − 1 », « a » */
  of: lineText,
});
export type KernelHypothesis = z.infer<typeof KernelHypothesisSchema>;
/**
 * objectifs que la feuille v18 sait juger ; v20 : simplifier une écriture (« A = √12 + √27 ») ;
 * v22 : résoudre un système d'équations (`system`)
 */
export const KERNEL_GOAL_KINDS = [
  'solve',
  'isolate_variable',
  'simplify',
  'solve_system',
  // moteur v25 : démontrer une propriété de géométrie (plugin « geometry »)
  'prove',
  // moteur v28 : tracer un programme Python (plugin « python »)
  'trace',
  // moteur v29 : écrire la ligne manquante d'un programme Python
  'complete',
  // moteur v30 : calculer (calcul matriciel, plugin « matrix »)
  'compute',
  // moteur v31 : dériver, trouver une primitive (plugin « analysis »)
  'differentiate',
  'antiderivative',
  // moteur v32 : calculer des grandeurs avec les formules de la carte (plugin « formula »)
  'evaluate',
  // moteur v34 : les modes spéciaux de l'ancien moteur, passés au noyau
  'convert',
  'residue',
  'calculate',
  'bound',
  // moteur v35 : arithmétique (plugin « arith ») — PGCD par Euclide, relation de Bézout
  'gcd',
  'bezout',
  // moteur v51 : PGCD et PPCM par les décompositions en facteurs premiers (plugin « arith »)
  'gcd_primes',
  'lcm',
  // moteur v36 : trancher chaque affirmation d'un QCM (plugin « claims »)
  'decide',
  // moteur v42 étendu (lot 6.20) : étudier une fonction, son tableau de variations
  'study',
  // moteur v42 étendu (lot 6.21) : remplir un tableau (statistiques, proportionnalité)
  'fill',
  // moteur v44 (lot 6.42) : l'équation de la tangente en un point (plugin « analysis »)
  'tangent',
  // moteur v54 (lot 6.58) : la limite d'une expression en un point (plugin « limit »)
  'limit',
  // moteur v55 (lot 6.60) : calculer une intégrale (plugin « integral »)
  'integrate',
  // moteur v59 (lot 6.64) : la limite d'une somme de Riemann (plugin « riemann »)
  'riemann',
  // moteur v62 (lot 6.67) : une équation différentielle (plugin « ode »)
  'solve_ode',
  // moteur v64 (lot 6.69) : une dérivée partielle, le gradient, le laplacien (plugin « partial »)
  'partial',
] as const;
/**
 * plugins du noyau : l'algèbre (lot 4), la géométrie déductive (lot 5.4, moteur v25),
 * la trace d'un programme Python (lot 5.7, moteur v28)
 */
export const KERNEL_PLUGINS = [
  'algebra',
  'geometry',
  'python',
  'matrix',
  'analysis',
  // moteur v32 (lot 6.1) : grandeurs données, formules de la SKILLCARD, grandeurs à trouver
  'formula',
  // moteur v34 (lot 6.4) : les modes spéciaux de l'ancien moteur, passés au noyau
  'units',
  'congruence',
  'complex',
  'bounds',
  // moteur v35 (lot 5.10) : PGCD par l'algorithme d'Euclide, relation de Bézout
  'arith',
  // moteur v36 (lot 6.8) : vérifier chaque affirmation d'un QCM « coche les vraies »
  'claims',
  // moteur v42 étendu (lot 6.20) : étudier une fonction — tableau de variations
  'variations',
  // moteur v42 étendu (lot 6.21) : remplir un tableau — statistiques, proportionnalité
  'tables',
  // moteur v54 (lot 6.58) : la limite d'une expression en un point
  'limit',
  // moteur v55 (lot 6.60) : une intégrale à calculer
  'integral',
  // moteur v57 (lot 6.62) : trancher des limites affirmées (« Quelles limites valent +∞ ? »)
  'limclaims',
  // moteur v59 (lot 6.64) : la limite d'une somme de Riemann
  'riemann',
  // moteur v62 (lot 6.67) : une équation différentielle linéaire d'ordre 1 ou 2
  'ode',
  // moteur v64 (lot 6.69) : les dérivées partielles d'une fonction de deux variables
  'partial',
] as const;
/**
 * nom d'une matrice de l'énoncé (moteur v30) : « A », « P », « π₀ », « X₁ » ; ou une
 * puissance donnée par l'énoncé : « A⁻¹ », « A² »
 */
export const MATRIX_NAME_RE = /^([A-Z]|π)[₀-₉]*(⁻¹|ᵀ|[⁰¹²³⁴⁵⁶⁷⁸⁹]+)?$/;
/**
 * Programme Python à tracer (moteur v28) : le code (affiché en tête de feuille),
 * l'appel suivi de la question (« pgcd(84, 36) », sinon on suit le programme lui-même)
 * et les variables proposées dans la palette (par défaut : celles qui changent).
 */
/** un tirage imposé (moteur v29) : « 4 », « 0,25 », « True » */
export const PYTHON_DRAW_RE = /^(True|False|[−-]?\d+(,\d+)?)$/;
export const KernelProgramSchema = z.object({
  code: z.string().min(1).max(2000),
  call: z.string().min(3).max(120).optional(),
  watch: z.array(name).min(1).max(8).optional(),
  /** moteur v29 : tirages imposés par l'énoncé, donnés dans l'ordre aux appels de random */
  draws: z.array(z.string().regex(PYTHON_DRAW_RE)).min(1).max(40).optional(),
  /** moteur v29 (objectif `complete`) : tests du programme complété (« s = 5050 ») */
  checks: z.array(lineText).min(1).max(6).optional(),
  /** moteur v29 (objectif `complete`) : ce que le programme complété doit afficher */
  output: z.array(z.string().min(1).max(200)).min(1).max(30).optional(),
  /** moteur v29 : l'algorithme tel que l'énoncé l'écrit (« Tant que n < 20 : … »), affiché */
  display: z.string().min(1).max(2000).optional(),
});
export type KernelProgram = z.infer<typeof KernelProgramSchema>;
const coord = z.number().finite().min(-1000).max(1000);
const xy = z.tuple([coord, coord]);
/**
 * Figure de référence (moteur v25) : des coordonnées qui réalisent l'énoncé. Elle dit
 * si une ligne est VRAIE ; seule une règle du cours la justifie.
 */
export const KernelFigureSchema = z.object({
  points: z
    .record(z.string().regex(/^[A-Z]$/), xy)
    .refine((p) => Object.keys(p).length >= 2 && Object.keys(p).length <= 12, {
      message: '2 à 12 points',
    }),
  /** droites nommées « d₁ » : deux points de passage */
  lines: z.record(z.string().regex(/^[a-zΔ][₀-₉]*$/), z.tuple([xy, xy])).optional(),
});
export type KernelFigure = z.infer<typeof KernelFigureSchema>;
/** nombre de solutions d'un système (moteur v22) */
export const SYSTEM_OUTCOMES = ['unique', 'none', 'infinite'] as const;
export type SystemOutcomeKind = (typeof SYSTEM_OUTCOMES)[number];
/**
 * Système d'équations linéaires (moteur v22) : les équations affichées en tête de
 * feuille, ses inconnues, et le nombre de solutions (vérifié par la porte).
 */
export const KernelSystemSpecSchema = z.object({
  unknowns: z.array(name).min(2).max(3),
  equations: z.array(lineText).min(2).max(4),
  outcome: z.enum(SYSTEM_OUTCOMES),
});
export type KernelSystemSpec = z.infer<typeof KernelSystemSpecSchema>;
/**
 * Récurrence (moteur v26) : l'hypothèse P(n) affichée en tête de feuille
 * (« Hypothèse de récurrence : uₙ = 3ⁿ »), la variable (n par défaut) et le premier rang.
 */
export const KernelInductionSpecSchema = z.object({
  /** P(n) : « uₙ = 3ⁿ », « 0 ≤ uₙ ≤ 2 », « 4ⁿ − 1 = 3k » */
  hypothesis: lineText,
  /** la variable de la récurrence (défaut : n), un entier naturel */
  variable: name.optional(),
  /** premier rang (défaut : 0) */
  from: z.number().int().min(0).max(20).optional(),
});
export type KernelInductionSpec = z.infer<typeof KernelInductionSpecSchema>;
/**
 * Une affirmation d'un QCM « coche les vraies » (moteur v36) : la ou les relations qui la
 * décident (vraies ensemble), entre nombres, ou avec des lettres dont `at` donne la
 * valeur proposée (« x = 3 est solution de 2x + 1 = 7 » : rels « 2x + 1 = 7 », at x → 3).
 */
export const KernelClaimSchema = z
  .object({
    rels: z.array(lineText).min(1).max(3),
    at: z
      .record(name, lineText)
      .refine((a) => Object.keys(a).length >= 1 && Object.keys(a).length <= 3, {
        message: '1 à 3 lettres',
      })
      .optional(),
    /**
     * moteur v39 : la FORME exigée du membre de droite (le choix du QCM) — fraction
     * irréductible, notation scientifique, décomposition en facteurs premiers. Avec UNE
     * égalité seulement.
     */
    form: z.enum(CLAIM_FORMS).optional(),
    /**
     * moteur v40 : affirmation de PROPRIÉTÉ — une écriture seule (`rels` : une expression,
     * sans relation) avec sa propriété : une forme (`form`, « nombre premier » compris) ou
     * « divisible par » (`divisibleBy`, un entier ≥ 2). `not` : la propriété est niée
     * (« 51 n'est pas premier »).
     */
    divisibleBy: z
      .string()
      .regex(/^[1-9]\d{0,5}$/)
      .refine((d) => Number(d) >= 2, { message: 'un diviseur ≥ 2' })
      .optional(),
    /**
     * 30/10 (moteur v2 seulement) : les DIVISEURS d'un entier (`rels` : l'entier seul) —
     * leur nombre, leur somme ou leur produit vaut `value` ; `proper` : sans l'entier
     * lui-même (« somme des diviseurs de 28 sauf 28 »). Tranchée quand toutes les paires
     * « a × b = n » sont écrites.
     */
    divisors: z
      .object({
        of: z.enum(['count', 'sum', 'product']),
        value: z.string().regex(/^\d{1,30}$/),
        proper: z.literal(true).optional(),
      })
      .optional(),
    not: z.literal(true).optional(),
  })
  .superRefine((c, ctx) => {
    const prop = isPropertyClaim(c);
    if (prop) {
      if ([c.form, c.divisibleBy, c.divisors].filter(Boolean).length !== 1)
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['form'],
          message:
            'une affirmation de propriété (une écriture seule) a UNE propriété : `form`, `divisibleBy` ou `divisors`',
        });
      if (c.divisors && (c.not || c.at))
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['divisors'],
          message: '`divisors` va avec un entier écrit, sans `not` ni `at`',
        });
      return;
    }
    if (c.divisibleBy || c.divisors || c.not || c.form === 'prime')
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['divisibleBy'],
        message:
          '« nombre premier », `divisibleBy`, `divisors` et `not` vont avec une écriture seule',
      });
    if (c.form && (c.rels.length !== 1 || !/=/.test(c.rels[0]!) || /[<>≤≥]/.test(c.rels[0]!)))
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['form'],
        message: 'une forme va avec UNE égalité « a = b »',
      });
  });
/** une affirmation de propriété (moteur v40) : une seule écriture, sans relation */
export function isPropertyClaim(c: { rels: readonly string[] }): boolean {
  return c.rels.length === 1 && !/[=<>≤≥≠]/.test(c.rels[0]!);
}
export type KernelClaim = z.infer<typeof KernelClaimSchema>;
/**
 * moteur v42 étendu (lot 6.20, plugin « variations ») : ce qu'affirme un choix du QCM sur
 * la fonction étudiée, tranché par le tableau de variations.
 *   increasing / decreasing  f croissante / décroissante sur chaque morceau de `on`
 *                            (« −2 ≤ x ≤ 1 », « x ≤ 3 » ; absent : sur ℝ)
 *   constant                 f constante (jamais vrai ici)
 *   min / max                un minimum / maximum (global sur `on[0]`, local avec `local`),
 *                            atteint en `at[0]` et / ou valant `value`
 *   no_min / no_max          pas de minimum / maximum (sur `on[0]`)
 *   extrema                  les abscisses des extremums locaux sont exactement `at`
 *   no_extremum              f′(at[0]) = 0 mais pas d'extremum en at[0]
 *   sign                     le signe de f′ sur `on[0]` : « + », « − », « 0 », « change »
 *   sequence                 croissante puis décroissante… (`seq`) sur `on[0]`
 *   other                    ne se lit pas dans le tableau (« f est négative ») : fausse
 *                            par élimination, avec `single`
 */
export const VARIATION_CLAIM_KINDS = [
  'increasing',
  'decreasing',
  'constant',
  'min',
  'max',
  'no_min',
  'no_max',
  'extrema',
  'no_extremum',
  'sign',
  'sequence',
  'other',
] as const;
export const KernelVariationClaimSchema = z.object({
  /** l'affirmation telle qu'elle s'affiche en tête de feuille (« f décroissante sur [−2 ; 1] ») */
  text: z.string().min(1).max(120),
  kind: z.enum(VARIATION_CLAIM_KINDS),
  on: z.array(lineText).max(3).optional(),
  at: z.array(lineText).min(1).max(6).optional(),
  value: lineText.optional(),
  local: z.literal(true).optional(),
  sign: z.enum(['+', '−', '0', 'change']).optional(),
  seq: z
    .array(z.enum(['increasing', 'decreasing']))
    .min(1)
    .max(4)
    .optional(),
});
export type KernelVariationClaim = z.infer<typeof KernelVariationClaimSchema>;
/** moteur v42 étendu (lot 6.21) : les tableaux à remplir */
export const TABLE_KINDS = ['series', 'proportion', 'cross', 'values', 'freq'] as const;
/**
 * Le tableau de l'énoncé (`kernel.start`, « ⊟{…} ») se lit selon :
 *   series      `form` — `raw` (la série, une ligne), `counts` (valeurs, effectifs),
 *               `cumulative` (valeurs, effectifs cumulés) ;
 *   proportion  deux lignes de grandeurs (« ? » : à trouver) ; `coef` : la question porte
 *               sur la proportionnalité, la ligne des quotients est demandée ;
 *   cross       double entrée, dernière ligne et dernière colonne « Total » (« ? » : à trouver) ;
 *   values      (lot 6.22) tableau de valeurs : la 1re ligne, la variable (« x », « n ») et ses
 *               valeurs ; chaque autre ligne, une formule `exprs[i]` (null : ligne donnée par
 *               l'énoncé), réduite modulo `mod` pour un tableau de restes ;
 *   freq        (lot 6.23) tableau des fréquences : une colonne par catégorie (en-têtes, la
 *               dernière peut être « Total »), la ligne des effectifs, celle des effectifs
 *               totaux de chaque colonne (facultative : sinon l'effectif total est la somme
 *               des effectifs), puis la ligne des fréquences ; `percent` : en pourcentage.
 */
export const KernelTableSpecSchema = z.object({
  kind: z.enum(TABLE_KINDS),
  form: z.enum(['raw', 'counts', 'cumulative']).optional(),
  coef: z.literal(true).optional(),
  exprs: z.array(z.string().min(1).max(40).nullable()).min(1).max(5).optional(),
  mod: z.number().int().min(2).max(20).optional(),
  percent: z.literal(true).optional(),
});
export type KernelTableSpec = z.infer<typeof KernelTableSpecSchema>;
/**
 * Une affirmation d'un QCM tranchée par le tableau (lot 6.21) :
 *   value             les grandeurs `stats` ont ces valeurs — « N », « Me », « Q₁ »,
 *                     « Q₃ », « n(12) » (effectif de 12), « ECC(12) » (effectif cumulé
 *                     jusqu'à 12), « k » (coefficient), « y(10) » (2e ligne sous 10),
 *                     « T(Foot) » / « T » (totaux d'une double entrée)
 *   proportional      le tableau est proportionnel (et `stats`, s'il y en a, ont ces valeurs)
 *   not_proportional  le tableau n'est pas proportionnel
 *   other             ne se lit pas dans le tableau : fausse par élimination, avec `single`
 * Tableau de valeurs (lot 6.22), sur la ligne `row` (1 : la 1re ligne calculée) :
 *   pairs             les couples (x ; y) de `pairs` sont sur la courbe de la formule
 *   set               la ligne prend exactement les valeurs `values`
 *   within            toutes ses valeurs sont parmi `values` (« ≡ 0 ou 1 pour tout n »)
 *   avoids            aucune de ses valeurs n'est dans `values` (« jamais 2 »)
 *   reached           chaque valeur de `values` est prise
 *   same_rows         les deux lignes `rows` sont égales (« quelle formule ? »)
 * Tableau des fréquences (lot 6.23) — `value` lit aussi « f(Classe A) » (fréquence, dans
 * l'unité du tableau), « n(Classe A) », « N(Classe A) », « N » ; `places` : la valeur
 * affirmée est l'arrondi de la grandeur à `places` décimales (« ≈ 0,158 ») :
 *   largest           la colonne `cols[0]` a la plus grande fréquence (strictement)
 *   same_freq         les colonnes `cols` ont la même fréquence
 */
export const TABLE_CLAIM_KINDS = [
  'value',
  'proportional',
  'not_proportional',
  'other',
  'pairs',
  'set',
  'within',
  'avoids',
  'reached',
  'same_rows',
  'largest',
  'same_freq',
] as const;
const cellText = z.string().min(1).max(24);
export const KernelTableClaimSchema = z.object({
  text: z.string().min(1).max(120),
  kind: z.enum(TABLE_CLAIM_KINDS),
  stats: z
    .record(z.string().min(1).max(40), z.string().min(1).max(40))
    .refine((r) => Object.keys(r).length <= 4, { message: '4 grandeurs au plus' })
    .optional(),
  pairs: z
    .array(z.tuple([cellText, cellText]))
    .min(1)
    .max(8)
    .optional(),
  values: z.array(cellText).min(1).max(12).optional(),
  row: z.number().int().min(1).max(5).optional(),
  rows: z.tuple([z.number().int().min(1).max(5), z.number().int().min(1).max(5)]).optional(),
  cols: z.array(z.string().min(1).max(40)).min(1).max(8).optional(),
  places: z.number().int().min(0).max(6).optional(),
});
export type KernelTableClaim = z.infer<typeof KernelTableClaimSchema>;
/**
 * moteur v57 (lot 6.62, plugin « limclaims ») : une limite affirmée par un choix du QCM —
 * l'expression, sa variable (x, ou n pour une suite), le point (« +∞ », « 0⁺ »…) et la
 * limite affirmée (« +∞ », « −∞ », « 0 », « 3 », « 0⁺ »).
 */
export const KernelLimitClaimSchema = z.object({
  expr: z.string().min(1).max(200),
  x: z.string().regex(/^[a-z]$/, { message: 'une lettre : « x », « n », « t »' }),
  point: z.string().regex(/^([+−]∞|−?\d+(,\d+)?[⁺⁻]?)$/, {
    message: 'un point : « +∞ », « −∞ », « 2 », « 2⁺ », « 0⁻ »',
  }),
  value: z.string().regex(/^([+−]∞|−?\d+(,\d+)?|0[⁺⁻])$/, {
    message: 'une limite : « +∞ », « −∞ », « 0 », « 3 », « 0⁺ »',
  }),
});
export type KernelLimitClaim = z.infer<typeof KernelLimitClaimSchema>;
/**
 * moteur v33 : la forme attendue d'une écriture à simplifier (objectif `simplify`) —
 * développée et réduite, ou factorisée.
 */
/** moteur v43 : `canonical`, la forme canonique a(x − α)² + β d'un trinôme ;
 *  moteur v46 : `fraction`, une seule fraction irréductible (uₙ₊₁ − uₙ, même dénominateur) */
export const KERNEL_FORMS = ['expanded', 'factored', 'canonical', 'fraction'] as const;
export type KernelForm = (typeof KERNEL_FORMS)[number];
/** moteur v47 : l'inconnue auxiliaire d'une feuille d'algèbre (X = eˣ, X = ln(x)) */
export const KERNEL_AUXILIARY_OF = ['exp', 'ln'] as const;
export const KernelAuxiliarySchema = z.object({
  /** le nom de l'inconnue auxiliaire : une lettre (« X », « u »), jamais « e » */
  name: z.string().regex(/^[A-DF-Za-df-z]$/, { message: 'une lettre (« X », « u »), pas « e »' }),
  /** ce qu'elle remplace : `exp` (X = e^x) ou `ln` (X = ln(x)), x étant l'inconnue */
  of: z.enum(KERNEL_AUXILIARY_OF),
});
export type KernelAuxiliary = z.infer<typeof KernelAuxiliarySchema>;

/**
 * moteur v62 (plugin « ode ») : les conditions initiales (« y(0) = 1 », « y′(0) = 0 ») et
 * les constantes des solutions (« C » ; « A », « B » ; « C₁ », « C₂ »).
 */
export const KernelOdeSpecSchema = z.object({
  initial: z.array(lineText).max(2).optional(),
  /** moteur v63 : la variable (« t ») ; « x » par défaut */
  variable: z
    .string()
    .regex(/^[a-z]$/, { message: 'la variable : une lettre minuscule (« t »)' })
    .optional(),
  constants: z
    .array(z.string().regex(/^[A-Z][₀-₉]?$/, { message: 'une constante : « C », « A », « C₁ »' }))
    .min(1)
    .max(3)
    .optional(),
});
export type KernelOdeSpec = z.infer<typeof KernelOdeSpecSchema>;

/**
 * moteur v64 (plugin « partial ») : la courbe d'une règle de la chaîne — x = x(t), y = y(t)
 * (expressions en t), et le nom de g(t) = f(x(t), y(t)).
 */
export const KernelPartialSpecSchema = z.object({
  curve: z
    .object({
      t: z.string().regex(/^[a-z]$/, { message: 'la variable : une lettre minuscule (« t »)' }),
      g: z.string().regex(/^[a-zA-Z]$/, { message: 'la fonction composée : une lettre (« g »)' }),
      x: z.string().min(1).max(80),
      y: z.string().min(1).max(80),
    })
    .optional(),
});
export type KernelPartialSpec = z.infer<typeof KernelPartialSpecSchema>;

export const KernelSpecSchema = z.object({
  /** plugin du noyau : l'algèbre (lot 4) ; la géométrie déductive (moteur v25) */
  plugin: z.enum(KERNEL_PLUGINS),
  /** résoudre (inéquation, équation) ou isoler une grandeur dans une formule */
  goal: z.object({ kind: z.enum(KERNEL_GOAL_KINDS), target: name }),
  /** la ligne de départ, affichée en tête de feuille : « 3x + 2 < 8 » */
  start: lineText,
  /** la ligne d'arrivée attendue : « x < 2 », « x = 3 ÷ (m − 1) » */
  answer: lineText,
  /** hypothèses de l'énoncé : « m − 1 » positif, « a » non nul */
  hypotheses: z.array(KernelHypothesisSchema).max(6).optional(),
  /** corrigé de référence (coup de pouce, plafond HORA) ; absent : celui de la recherche */
  path: z.array(lineText).min(1).max(40).optional(),
  /** moteur v22 : système d'équations (objectif `solve_system`, `start` = 1re équation) */
  system: KernelSystemSpecSchema.optional(),
  /**
   * moteur v23 : `factor` — second degré, produit, quotient. L'avancée se mesure à la
   * forme (tout d'un côté, factorisé, ensemble de solutions), plus à l'inconnue isolée ;
   * Δ, x₁, x₂ (déclarés dans `symbols`) s'y calculent pour le trinôme de la feuille.
   * moteur v24 : `trig` — équation trigonométrique, x réel (radians) ; un entier k
   * (hypothèse `integer`) écrit les familles de solutions.
   */
  method: z.enum(KERNEL_METHODS).optional(),
  /**
   * moteur v33 (objectif `simplify`, plugin « algebra ») : la forme attendue — `expanded`
   * (développer puis réduire, règles des puissances comprises) ou `factored` (factoriser :
   * facteur commun, a² − b², a² ± 2ab + b²) ; moteur v43 : `canonical` (forme canonique
   * a(x − α)² + β d'un trinôme en une lettre). `path` obligatoire.
   */
  form: z.enum(KERNEL_FORMS).optional(),
  /**
   * moteur v24 : l'intervalle de l'énoncé (« −π < x ≤ π », « 0 ≤ x < 2π »), affiché en
   * tête de feuille ; absent : on résout sur ℝ.
   */
  domain: lineText.optional(),
  /**
   * moteur v44 (plugin « analysis », objectif `tangent`) : l'abscisse a du point de
   * tangence, telle qu'elle s'écrit dans « f(a) » (« 0 », « 2 », « e »).
   */
  at: z
    .string()
    .regex(/^(−?\d+(,\d+)?|e|π)$/, { message: 'une abscisse : un nombre, « e » ou « π »' })
    .optional(),
  /**
   * moteur v54 (plugin « limit ») : le point où l'on cherche la limite — « +∞ », « −∞ »,
   * un réel (« 2 », « 0,5 ») des deux côtés, ou d'un côté (« 2⁺ », « 0⁻ »).
   */
  point: z
    .string()
    .regex(/^([+−]∞|−?\d+(,\d+)?[⁺⁻]?)$/, {
      message: 'un point : « +∞ », « −∞ », « 2 », « 2⁺ », « 0⁻ »',
    })
    .optional(),
  /**
   * moteur v44 (plugin « analysis », objectif `antiderivative`) : la condition qui fixe la
   * primitive, « F(0) = 2 » (la tête est la cible, en un point écrit comme `at`).
   */
  condition: lineText.optional(),
  /**
   * moteur v25 (plugin « geometry ») : la figure de référence. L'énoncé est `start`
   * (1re propriété) puis `givens` ; `answer` est la propriété à démontrer
   * (« (MN) ∦ (BC) ») ou la longueur cherchée (« IJ = 4 ») ; `path` est obligatoire.
   */
  figure: KernelFigureSchema.optional(),
  /**
   * moteur v26 (objectif `prove`, plugin « algebra ») : l'hérédité d'une récurrence.
   * `start` = la définition de la suite (ou P(n) elle-même), `answer` = P(n + 1),
   * `path` obligatoire.
   */
  induction: KernelInductionSpecSchema.optional(),
  /**
   * moteur v28 (plugin « python », objectif `trace`) : le programme à tracer. `start` =
   * l'appel suivi (`program.call`) ou la variable cherchée ; `answer` = « c = 2 »,
   * « pgcd(84, 36) = 12 », « a = 1,5 ; b = 2 » ; `path` = la trace, obligatoire.
   */
  program: KernelProgramSchema.optional(),
  /**
   * moteur v30 (plugin « matrix », objectif `compute`) : les matrices de l'énoncé, en
   * écriture « [1, 2 ; 3, 4] » (lignes séparées par « ; », coefficients par « , ») ou en
   * expression des précédentes (« B = A − 2I »). `start` = le calcul demandé (« AB ») ou
   * l'équation « AX = B » ; `answer` = « AB = [8 ; 9] » ; `path` obligatoire.
   */
  matrices: z
    .record(z.string().regex(MATRIX_NAME_RE), lineText)
    .refine((m) => Object.keys(m).length <= 8, { message: '8 matrices au plus' })
    .optional(),
  /**
   * moteur v36 (plugin « claims », objectif `decide`) : une affirmation par choix du QCM,
   * dans l'ordre des choix. `start` = la 1re relation de la 1re affirmation ; `answer` =
   * les lettres des affirmations vraies, dans l'ordre (« A, C ») ; `path` obligatoire.
   */
  claims: z.array(KernelClaimSchema).min(2).max(8).optional(),
  /**
   * moteur v40 : QCM à réponse UNIQUE (affiché « Réponse unique ») — les affirmations se
   * tranchent aussi par élimination : une vraie trouvée, les autres sont fausses ; toutes
   * fausses sauf une, elle est vraie.
   */
  single: z.literal(true).optional(),
  /**
   * moteur v42 étendu (plugin « variations », objectif `study`) : une affirmation sur la
   * fonction par choix du QCM, dans l'ordre des choix. `start` = « f(x) = … » (ou
   * « f′(x) = … » quand l'énoncé ne donne que f′) ; f′ donnée par l'énoncé : dans `givens` ;
   * `answer` = les lettres des affirmations vraies (« A ») ; `path` obligatoire (dériver si
   * besoin, puis le tableau de variations).
   */
  vclaims: z.array(KernelVariationClaimSchema).min(2).max(8).optional(),
  /**
   * moteur v42 étendu (plugin « tables », objectif `fill`) : le tableau de l'énoncé
   * (`start` = « ⊟{…} ») et comment le lire.
   */
  table: KernelTableSpecSchema.optional(),
  /** moteur v42 étendu (plugin « tables ») : une affirmation par choix du QCM, dans l'ordre */
  tclaims: z.array(KernelTableClaimSchema).min(2).max(8).optional(),
  /**
   * moteur v57 (plugin « limclaims », objectif `decide`) : une limite affirmée par choix du
   * QCM, dans l'ordre des choix. `start` = l'expression de la 1re ; `answer` = les lettres
   * des affirmations vraies (« A, B ») ; `path` obligatoire (les limites à établir).
   */
  lclaims: z.array(KernelLimitClaimSchema).min(2).max(8).optional(),
  /**
   * moteur v62 (plugin « ode », objectif `solve_ode`) : `start` = l'équation (« y′ = −2y + 8 »),
   * `answer` = l'équation réécrite (« y′ = … »), toutes les solutions (« y = C × e^(−2x) + 4 »)
   * ou une solution (« y = −3 × e^(−2x) + 4 ») ; `goal.target` = la fonction (« y ») ;
   * `domain` facultatif (« x > 0 ») ; `path` obligatoire.
   */
  ode: KernelOdeSpecSchema.optional(),
  /**
   * moteur v64 (plugin « partial », objectif `partial`) : `start` = la fonction
   * (« f(x, y) = x² × y »), `goal.target` = la dérivée demandée (« ∂f/∂x », « ∂²f/∂x∂y »,
   * « ∇f », « Δf », « ∂f/∂x(1, 2) », « g′(t) »…), `answer` = « question = valeur » ;
   * `curve` facultatif (règle de la chaîne) ; `path` obligatoire.
   */
  partial: KernelPartialSpecSchema.optional(),
  /**
   * moteur v47 (plugin « algebra », méthode `factor`) : une INCONNUE AUXILIAIRE — `name`
   * (« X ») remplace `of` (« eˣ », « ln(x) ») : « e^(2x) − 3eˣ + 2 = 0 » se résout comme
   * « X² − 3X + 2 = 0 » ; l'élève peut écrire en X (« On pose X = eˣ ») ou garder eˣ.
   */
  auxiliary: KernelAuxiliarySchema.optional(),
  /**
   * moteur v47 : version minimale DÉCLARÉE par la feuille (≥ 47) — ce que le modèle ne
   * montre pas (libellés lisibles : « Diviser par 0,4 », « Diviser par e^(−6) »).
   */
  minEngine: z.number().int().min(47).max(999).optional(),
  /**
   * moteur v49 (plugin « algebra », objectif `solve`) : les CONDITIONS que l'élève doit
   * écrire sur leur ligne (« x > 0 » pour ln(x), « x ≥ 0 » pour √x, « x − 3 ≥ 0 » quand
   * √(x + 3) = x − 3) : une conséquence du départ, en l'inconnue seule. Écrite, une
   * condition rend juste le pas qui en a besoin (composer par exp, élever au carré) ; la
   * réponse en tient compte (« 0 < x < e »). Jamais affichées.
   */
  conditions: z.array(lineText).min(1).max(4).optional(),
});
export type KernelSpec = z.infer<typeof KernelSpecSchema>;

// ── Le modèle ───────────────────────────────────────────────────────────────

/** relations de comparaison (moteur v5) */
export const CMP_RELATIONS = ['>', '<', '≥', '≤', '≈'] as const;
export type CmpRelation = (typeof CMP_RELATIONS)[number];

/**
 * Une comparaison qui fait avancer le raisonnement (moteur v5) : « Δ > 0 »
 * décide qu'il y a deux solutions. Écrite par l'élève une fois ses grandeurs
 * calculées, elle est vérifiée par le moteur et rapporte `hora`.
 */
export const CheckSpecSchema = z.object({
  /** la comparaison, avec les grandeurs du modèle : « Δ > 0 » */
  line: lineText,
  /** étiquette de la ligne validée : « Signe du discriminant » */
  label: z.string().min(1).max(60),
  /** pourquoi elle compte : « Δ > 0 : l'équation a deux solutions. » */
  why: z.string().min(1).max(400),
  hora: z.number().int().min(1).max(6).optional(),
});
export type CheckSpec = z.infer<typeof CheckSpecSchema>;

/**
 * Réponse arrondie (moteur v10) : la question demande une valeur approchée
 * (« arrondi au dixième », « au cm³ près », π ≈ 3,14). La valeur exacte se
 * calcule comme d'habitude ; la feuille se termine par « AC ≈ 8,4 », jugée
 * juste si c'est l'arrondi à `places` chiffres après la virgule.
 */
export const RoundingSpecSchema = z.object({
  /** chiffres après la virgule : 0 à l'unité, 1 au dixième, 2 au centième… */
  places: z.number().int().min(0).max(4),
  /** grandeur arrondie (par défaut, la première du but) */
  symbol: name.optional(),
  hora: z.number().int().min(1).max(6).optional(),
});
export type RoundingSpec = z.infer<typeof RoundingSpecSchema>;

export const ScratchModelSchema = z
  .object({
    symbols: z.record(name, SymbolSpecSchema),
    /** grandeur(s) cherchée(s) par la question */
    goal: z.array(name).min(1).max(4),
    /** valeurs attendues quand plusieurs solutions (x₁ et x₂…) */
    goalValues: z.array(z.number()).max(8).optional(),
    /** la question attend une EXPRESSION (« A = (x + 3)(x − 5) »), pas une valeur */
    answers: z.array(lineText).max(8).optional(),
    aim: z.enum(['factor', 'expand']).optional(),
    /** moteur v15 : l'opération s'écrit avant son résultat (« x = 12 ÷ 3 » puis « x = 4 ») */
    stepwise: z.boolean().optional(),
    /** moteur v16 : corrigé écrit à la main, rejoué par le juge (le solveur ne le cherche pas) */
    path: z.array(lineText).min(1).max(16).optional(),
    factorHint: z.string().max(300).optional(),
    /** égalités de l'énoncé affichées en tête de feuille */
    givens: z.array(lineText).max(8).optional(),
    /** résultats des questions précédentes, admis (« on admet que BC = 10 ») */
    admitted: z.array(lineText).max(8).optional(),
    preds: z.array(PredSchema).max(6).optional(),
    /** échantillons de valeurs pour juger une égalité « pour tout x » (identités, récurrence) */
    envs: z.array(z.record(name, z.number())).min(2).max(16).optional(),
    /** forme compacte des `envs`, développée par `materializeModel` */
    samples: SamplesSpecSchema.optional(),
    traps: z.array(TrapSpecSchema).max(16).optional(),
    /** comparaisons décisives (moteur v5) : « Δ > 0 » */
    checks: z.array(CheckSpecSchema).max(6).optional(),
    /** les choix du QCM de la question */
    choices: z.array(ChoiceSpecSchema).max(8).optional(),
    /** question de congruences (moteur v3) */
    congruence: CongruenceSpecSchema.optional(),
    /** nombres complexes (moteur v11) */
    complex: ComplexSpecSchema.optional(),
    /** question de grandeurs et d'unités (moteur v4) */
    units: UnitsSpecSchema.optional(),
    /** question de raisonnement géométrique (moteur v4) */
    geometry: GeometrySpecSchema.optional(),
    /** réponse ARRONDIE (moteur v10) : « AC ≈ 8,4 » au dixième (places = 1) */
    rounding: RoundingSpecSchema.optional(),
    /** encadrer une racine carrée (moteur v13) */
    bounds: BoundsSpecSchema.optional(),
    /** feuille jugée par le noyau (moteur v18) */
    kernel: KernelSpecSchema.optional(),
  })
  .superRefine((m, ctx) => {
    if (m.kernel && (m.kernel.goal.kind === 'solve_system') !== !!m.kernel.system)
      ctx.addIssue({
        code: 'custom',
        path: ['kernel', 'system'],
        message: 'un système (`system`) va avec l’objectif solve_system, et seulement lui',
      });
    if (m.kernel?.system && !m.kernel.system.unknowns.includes(m.kernel.goal.target))
      ctx.addIssue({
        code: 'custom',
        path: ['kernel', 'goal', 'target'],
        message: `l'inconnue ${m.kernel.goal.target} doit être une inconnue du système`,
      });
    if (
      m.kernel?.conditions &&
      (m.kernel.plugin !== 'algebra' ||
        m.kernel.goal.kind !== 'solve' ||
        m.kernel.auxiliary ||
        m.kernel.method === 'test' ||
        m.kernel.method === 'trig')
    )
      ctx.addIssue({
        code: 'custom',
        path: ['kernel', 'conditions'],
        message:
          'des conditions (`conditions`) vont avec le plugin « algebra » et l’objectif solve (sans inconnue auxiliaire, ni méthode « test » ou « trig »)',
      });
    if (m.kernel?.method && m.kernel.goal.kind !== 'solve')
      ctx.addIssue({
        code: 'custom',
        path: ['kernel', 'method'],
        message: `la méthode « ${m.kernel.method} » résout une équation ou une inéquation (objectif solve)`,
      });
    if (
      m.kernel?.form &&
      (m.kernel.plugin !== 'algebra' || m.kernel.goal.kind !== 'simplify' || !m.kernel.path)
    )
      ctx.addIssue({
        code: 'custom',
        path: ['kernel', 'form'],
        message:
          'une forme (`form`) va avec le plugin « algebra », l’objectif simplify et un corrigé (`path`)',
      });
    if (
      m.kernel?.domain &&
      m.kernel.method !== 'trig' &&
      m.kernel.plugin !== 'analysis' &&
      m.kernel.plugin !== 'variations' &&
      m.kernel.plugin !== 'ode'
    )
      ctx.addIssue({
        code: 'custom',
        path: ['kernel', 'domain'],
        message:
          'un intervalle (`domain`) va avec la méthode « trig » ou les plugins « analysis », « variations » et « ode »',
      });
    if (m.kernel) {
      const ana = m.kernel.plugin === 'analysis';
      const anaGoal =
        m.kernel.goal.kind === 'differentiate' ||
        m.kernel.goal.kind === 'antiderivative' ||
        m.kernel.goal.kind === 'tangent';
      if (ana !== anaGoal)
        ctx.addIssue({
          code: 'custom',
          path: ['kernel', 'goal', 'kind'],
          message:
            'les objectifs differentiate, antiderivative et tangent vont avec le plugin « analysis », et lui seul',
        });
      if ((m.kernel.goal.kind === 'tangent') !== !!m.kernel.at)
        ctx.addIssue({
          code: 'custom',
          path: ['kernel', 'at'],
          message: 'l’abscisse du point (`at`) va avec l’objectif tangent, et lui seul',
        });
      if (m.kernel.condition && m.kernel.goal.kind !== 'antiderivative')
        ctx.addIssue({
          code: 'custom',
          path: ['kernel', 'condition'],
          message: 'une condition (`condition`) va avec l’objectif antiderivative',
        });
      if (
        ana &&
        (m.kernel.method ||
          m.kernel.system ||
          m.kernel.hypotheses?.length ||
          m.kernel.figure ||
          m.kernel.induction ||
          m.kernel.program ||
          m.kernel.matrices ||
          m.rounding ||
          !m.kernel.path?.length)
      )
        ctx.addIssue({
          code: 'custom',
          path: ['kernel'],
          message:
            'une dérivée ou une primitive a son corrigé (`path`), sans méthode, système, hypothèses, figure, récurrence, programme, matrices ni arrondi',
        });
    }
    if (m.kernel) {
      const py = m.kernel.plugin === 'python';
      const pyGoal = m.kernel.goal.kind === 'trace' || m.kernel.goal.kind === 'complete';
      if (py !== !!m.kernel.program || py !== pyGoal)
        ctx.addIssue({
          code: 'custom',
          path: ['kernel', 'program'],
          message:
            'les objectifs trace et complete vont avec un programme Python (plugin « python », `program`), et eux seuls',
        });
      const prog = m.kernel.program;
      if (py && prog) {
        const complete = m.kernel.goal.kind === 'complete';
        const blanks = prog.code.split('\n').filter((l) => /^\s*_{3,}\s*$/.test(l)).length;
        if (complete && (blanks !== 1 || (!prog.checks && !prog.output) || prog.call))
          ctx.addIssue({
            code: 'custom',
            path: ['kernel', 'program'],
            message:
              'compléter un programme : une seule ligne manquante « ____ », des tests (`checks` ou `output`), pas d’appel suivi',
          });
        if (!complete && (blanks || prog.checks || prog.output))
          ctx.addIssue({
            code: 'custom',
            path: ['kernel', 'program'],
            message: 'une ligne manquante et des tests vont avec l’objectif complete',
          });
        if (prog.draws && !/\brandom\b/.test(prog.code))
          ctx.addIssue({
            code: 'custom',
            path: ['kernel', 'program', 'draws'],
            message: 'des tirages imposés vont avec un programme qui utilise random',
          });
      }
      if (
        py &&
        (m.kernel.method ||
          m.kernel.domain ||
          m.kernel.system ||
          m.kernel.hypotheses?.length ||
          m.kernel.figure ||
          m.kernel.induction ||
          m.rounding ||
          !m.kernel.path?.length)
      )
        ctx.addIssue({
          code: 'custom',
          path: ['kernel'],
          message:
            'une trace Python a son corrigé (`path`), sans méthode, intervalle, système, hypothèses, figure, récurrence ni arrondi',
        });
      if (
        py &&
        m.kernel.program &&
        m.kernel.start !== (m.kernel.program.call ?? m.kernel.goal.target)
      )
        ctx.addIssue({
          code: 'custom',
          path: ['kernel', 'start'],
          message: '`start` est l’appel suivi (`program.call`), sinon la variable cherchée',
        });
    }
    if (m.kernel) {
      const mat = m.kernel.plugin === 'matrix';
      if (mat !== (m.kernel.goal.kind === 'compute'))
        ctx.addIssue({
          code: 'custom',
          path: ['kernel', 'goal', 'kind'],
          message:
            'l’objectif compute va avec un calcul matriciel (plugin « matrix »), et lui seul',
        });
      if (m.kernel.matrices && !mat)
        ctx.addIssue({
          code: 'custom',
          path: ['kernel', 'matrices'],
          message: 'des matrices (`matrices`) vont avec le plugin « matrix »',
        });
      if (
        mat &&
        (m.kernel.method ||
          m.kernel.domain ||
          m.kernel.system ||
          m.kernel.hypotheses?.length ||
          m.kernel.figure ||
          m.kernel.induction ||
          m.kernel.program ||
          m.rounding ||
          !m.kernel.path?.length)
      )
        ctx.addIssue({
          code: 'custom',
          path: ['kernel'],
          message:
            'un calcul matriciel a son corrigé (`path`), sans méthode, intervalle, système, hypothèses, figure, récurrence, programme ni arrondi',
        });
    }
    if (m.kernel) {
      const geo = m.kernel.plugin === 'geometry';
      const rec = !!m.kernel.induction;
      if (geo !== !!m.kernel.figure || (geo || rec) !== (m.kernel.goal.kind === 'prove'))
        ctx.addIssue({
          code: 'custom',
          path: ['kernel', 'plugin'],
          message:
            'l’objectif prove va avec une démonstration de géométrie (plugin « geometry », une figure) ou une récurrence (`induction`), et elles seules',
        });
      if (
        rec &&
        (geo ||
          m.kernel.method ||
          m.kernel.domain ||
          m.kernel.system ||
          m.rounding ||
          !m.kernel.path?.length)
      )
        ctx.addIssue({
          code: 'custom',
          path: ['kernel', 'induction'],
          message:
            'une récurrence (plugin « algebra ») a son corrigé (`path`), sans figure, méthode, intervalle, système ni arrondi',
        });
      if (
        geo &&
        (m.kernel.method ||
          m.kernel.domain ||
          m.kernel.system ||
          m.kernel.hypotheses?.length ||
          m.rounding ||
          !m.kernel.path?.length)
      )
        ctx.addIssue({
          code: 'custom',
          path: ['kernel'],
          message:
            'une démonstration de géométrie a son corrigé (`path`), sans méthode, intervalle, système, hypothèses ni arrondi',
        });
    }
    if (m.kernel) {
      const ar = m.kernel.plugin === 'arith';
      const arGoal =
        m.kernel.goal.kind === 'gcd' ||
        m.kernel.goal.kind === 'bezout' ||
        m.kernel.goal.kind === 'gcd_primes' ||
        m.kernel.goal.kind === 'lcm';
      if (ar !== arGoal)
        ctx.addIssue({
          code: 'custom',
          path: ['kernel', 'goal', 'kind'],
          message:
            'les objectifs gcd, bezout, gcd_primes et lcm vont avec l’arithmétique (plugin « arith »), et elle seule',
        });
      if (
        ar &&
        (m.kernel.method ||
          m.kernel.form ||
          m.kernel.domain ||
          m.kernel.system ||
          m.kernel.hypotheses?.length ||
          m.kernel.figure ||
          m.kernel.induction ||
          m.kernel.program ||
          m.kernel.matrices ||
          m.complex ||
          m.congruence ||
          m.units ||
          m.bounds ||
          m.geometry ||
          m.rounding ||
          !m.kernel.path?.length)
      )
        ctx.addIssue({
          code: 'custom',
          path: ['kernel'],
          message:
            'une feuille d’arithmétique a son corrigé (`path`), sans méthode, forme, intervalle, système, hypothèses, figure, récurrence, programme, matrices, mode spécial ni arrondi',
        });
    }
    if (m.kernel) {
      const li = m.kernel.plugin === 'limit';
      if (li !== (m.kernel.goal.kind === 'limit') || li !== !!m.kernel.point)
        ctx.addIssue({
          code: 'custom',
          path: ['kernel', 'point'],
          message:
            'l’objectif limit va avec une feuille de limites (plugin « limit », `point`), et elle seule',
        });
      if (
        li &&
        (m.kernel.method ||
          m.kernel.form ||
          m.kernel.domain ||
          m.kernel.system ||
          m.kernel.hypotheses?.length ||
          m.kernel.figure ||
          m.kernel.induction ||
          m.kernel.program ||
          m.kernel.matrices ||
          m.kernel.claims ||
          m.complex ||
          m.congruence ||
          m.units ||
          m.bounds ||
          m.geometry ||
          m.rounding ||
          !m.kernel.path?.length)
      )
        ctx.addIssue({
          code: 'custom',
          path: ['kernel'],
          message:
            'une feuille de limites a son corrigé (`path`), sans méthode, forme, intervalle, système, hypothèses, figure, récurrence, programme, matrices, affirmations, mode spécial ni arrondi',
        });
    }
    if (m.kernel) {
      const ig = m.kernel.plugin === 'integral';
      if (ig !== (m.kernel.goal.kind === 'integrate'))
        ctx.addIssue({
          code: 'custom',
          path: ['kernel', 'goal', 'kind'],
          message:
            'l’objectif integrate va avec une feuille d’intégrales (plugin « integral »), et elle seule',
        });
      if (
        ig &&
        (m.kernel.method ||
          m.kernel.form ||
          m.kernel.domain ||
          m.kernel.system ||
          m.kernel.hypotheses?.length ||
          m.kernel.figure ||
          m.kernel.induction ||
          m.kernel.program ||
          m.kernel.matrices ||
          m.kernel.claims ||
          m.kernel.point ||
          m.complex ||
          m.congruence ||
          m.units ||
          m.bounds ||
          m.geometry ||
          m.rounding ||
          !m.kernel.path?.length)
      )
        ctx.addIssue({
          code: 'custom',
          path: ['kernel'],
          message:
            'une feuille d’intégrales a son corrigé (`path`), sans méthode, forme, intervalle, système, hypothèses, figure, récurrence, programme, matrices, affirmations, point, mode spécial ni arrondi',
        });
    }
    if (m.kernel) {
      const od = m.kernel.plugin === 'ode';
      if (od !== (m.kernel.goal.kind === 'solve_ode'))
        ctx.addIssue({
          code: 'custom',
          path: ['kernel', 'goal', 'kind'],
          message:
            'l’objectif solve_ode va avec une feuille d’équation différentielle (plugin « ode »), et elle seule',
        });
      if (!od && m.kernel.ode)
        ctx.addIssue({
          code: 'custom',
          path: ['kernel', 'ode'],
          message: 'la clé `ode` va avec une feuille d’équation différentielle, et elle seule',
        });
      if (
        od &&
        (m.kernel.method ||
          m.kernel.form ||
          m.kernel.system ||
          m.kernel.hypotheses?.length ||
          m.kernel.figure ||
          m.kernel.induction ||
          m.kernel.program ||
          m.kernel.matrices ||
          m.kernel.claims ||
          m.kernel.lclaims ||
          m.kernel.point ||
          m.kernel.condition ||
          m.complex ||
          m.congruence ||
          m.units ||
          m.bounds ||
          m.geometry ||
          m.rounding ||
          !m.kernel.path?.length)
      )
        ctx.addIssue({
          code: 'custom',
          path: ['kernel'],
          message:
            'une feuille d’équation différentielle a son corrigé (`path`), sans méthode, forme, système, hypothèses, figure, récurrence, programme, matrices, affirmations, point, condition, mode spécial ni arrondi',
        });
    }
    if (m.kernel) {
      const pa = m.kernel.plugin === 'partial';
      if (pa !== (m.kernel.goal.kind === 'partial'))
        ctx.addIssue({
          code: 'custom',
          path: ['kernel', 'goal', 'kind'],
          message:
            'l’objectif partial va avec une feuille de dérivées partielles (plugin « partial »), et elle seule',
        });
      if (!pa && m.kernel.partial)
        ctx.addIssue({
          code: 'custom',
          path: ['kernel', 'partial'],
          message: 'la clé `partial` va avec une feuille de dérivées partielles, et elle seule',
        });
      if (
        pa &&
        (m.kernel.method ||
          m.kernel.form ||
          m.kernel.domain ||
          m.kernel.system ||
          m.kernel.hypotheses?.length ||
          m.kernel.figure ||
          m.kernel.induction ||
          m.kernel.program ||
          m.kernel.matrices ||
          m.kernel.claims ||
          m.kernel.lclaims ||
          m.kernel.point ||
          m.kernel.condition ||
          m.kernel.ode ||
          m.complex ||
          m.congruence ||
          m.units ||
          m.bounds ||
          m.geometry ||
          m.rounding ||
          !m.kernel.path?.length)
      )
        ctx.addIssue({
          code: 'custom',
          path: ['kernel'],
          message:
            'une feuille de dérivées partielles a son corrigé (`path`), sans méthode, forme, intervalle, système, hypothèses, figure, récurrence, programme, matrices, affirmations, point, condition, équation différentielle, mode spécial ni arrondi',
        });
    }
    if (m.kernel) {
      const rm = m.kernel.plugin === 'riemann';
      if (rm !== (m.kernel.goal.kind === 'riemann'))
        ctx.addIssue({
          code: 'custom',
          path: ['kernel', 'goal', 'kind'],
          message:
            'l’objectif riemann va avec une feuille de sommes de Riemann (plugin « riemann »), et elle seule',
        });
      if (
        rm &&
        (m.kernel.method ||
          m.kernel.form ||
          m.kernel.domain ||
          m.kernel.system ||
          m.kernel.hypotheses?.length ||
          m.kernel.figure ||
          m.kernel.induction ||
          m.kernel.program ||
          m.kernel.matrices ||
          m.kernel.claims ||
          m.kernel.lclaims ||
          m.kernel.point ||
          m.complex ||
          m.congruence ||
          m.units ||
          m.bounds ||
          m.geometry ||
          m.rounding ||
          !m.kernel.path?.length)
      )
        ctx.addIssue({
          code: 'custom',
          path: ['kernel'],
          message:
            'une feuille de sommes de Riemann a son corrigé (`path`), sans méthode, forme, intervalle, système, hypothèses, figure, récurrence, programme, matrices, affirmations, point, mode spécial ni arrondi',
        });
    }
    if (m.kernel) {
      const fo = m.kernel.plugin === 'formula';
      if (fo !== (m.kernel.goal.kind === 'evaluate'))
        ctx.addIssue({
          code: 'custom',
          path: ['kernel', 'goal', 'kind'],
          message:
            'l’objectif evaluate va avec une feuille à formules (plugin « formula »), et elle seule',
        });
      if (
        fo &&
        (m.kernel.method ||
          m.kernel.domain ||
          m.kernel.system ||
          m.kernel.hypotheses?.length ||
          m.kernel.figure ||
          m.kernel.induction ||
          m.kernel.program ||
          m.kernel.matrices ||
          m.complex ||
          m.congruence ||
          m.units ||
          m.geometry ||
          m.bounds ||
          !m.kernel.path?.length)
      )
        ctx.addIssue({
          code: 'custom',
          path: ['kernel'],
          message:
            'une feuille à formules a son corrigé (`path`), sans méthode, intervalle, système, hypothèses, figure, récurrence, programme, matrices, ni mode spécial (complexes, congruences, unités, géométrie, encadrement)',
        });
      if (fo && m.kernel.start.trim() !== m.kernel.goal.target)
        ctx.addIssue({
          code: 'custom',
          path: ['kernel', 'start'],
          message: '`start` est la grandeur cherchée (`goal.target`) dans une feuille à formules',
        });
    }
    if (m.kernel) {
      const sp = kernelSpecialPlugin(m);
      const spGoal = (Object.values(KERNEL_SPECIAL_GOALS) as string[]).includes(m.kernel.goal.kind);
      if ((sp !== null) !== spGoal || (sp && KERNEL_SPECIAL_GOALS[sp] !== m.kernel.goal.kind))
        ctx.addIssue({
          code: 'custom',
          path: ['kernel', 'goal', 'kind'],
          message:
            'les objectifs convert, residue, calculate et bound vont avec les plugins units, congruence, complex et bounds (dans cet ordre), et eux seuls',
        });
      const modeKeys = (['units', 'congruence', 'complex', 'bounds'] as const).filter(
        (k) => !!m[k],
      );
      if (sp && (modeKeys.length !== 1 || modeKeys[0] !== sp))
        ctx.addIssue({
          code: 'custom',
          path: ['kernel', 'plugin'],
          message: `le plugin « ${sp} » garde la clé de son mode (\`${sp}\`), et elle seule`,
        });
      if (!sp && modeKeys.length)
        ctx.addIssue({
          code: 'custom',
          path: ['kernel'],
          message:
            'une feuille du noyau n’a pas de mode spécial, sauf avec son plugin (units, congruence, complex, bounds)',
        });
      if (
        sp &&
        (m.kernel.method ||
          m.kernel.domain ||
          m.kernel.system ||
          m.kernel.hypotheses?.length ||
          m.kernel.figure ||
          m.kernel.induction ||
          m.kernel.program ||
          m.kernel.matrices ||
          m.kernel.form ||
          m.geometry ||
          m.rounding ||
          !m.kernel.path?.length)
      )
        ctx.addIssue({
          code: 'custom',
          path: ['kernel'],
          message:
            'un mode spécial du noyau a son corrigé (`path`), sans méthode, intervalle, système, hypothèses, figure, récurrence, programme, matrices, forme ni arrondi',
        });
    }
    if (m.kernel) {
      const cl = m.kernel.plugin === 'claims';
      const lc = m.kernel.plugin === 'limclaims';
      if (cl !== !!m.kernel.claims || (cl || lc) !== (m.kernel.goal.kind === 'decide'))
        ctx.addIssue({
          code: 'custom',
          path: ['kernel', 'claims'],
          message:
            'l’objectif decide va avec des affirmations à trancher (plugin « claims », `claims` ; plugin « limclaims », `lclaims`), et elles seules',
        });
      if (lc !== !!m.kernel.lclaims)
        ctx.addIssue({
          code: 'custom',
          path: ['kernel', 'lclaims'],
          message:
            '`lclaims` va avec des limites affirmées (plugin « limclaims »), et elles seules',
        });
      if (
        lc &&
        (m.kernel.method ||
          m.kernel.form ||
          m.kernel.domain ||
          m.kernel.system ||
          m.kernel.hypotheses?.length ||
          m.kernel.figure ||
          m.kernel.induction ||
          m.kernel.program ||
          m.kernel.matrices ||
          m.kernel.claims ||
          m.kernel.vclaims ||
          m.kernel.tclaims ||
          m.kernel.point ||
          m.complex ||
          m.congruence ||
          m.units ||
          m.bounds ||
          m.geometry ||
          m.rounding ||
          !m.kernel.path?.length)
      )
        ctx.addIssue({
          code: 'custom',
          path: ['kernel'],
          message:
            'des limites affirmées ont leur corrigé (`path`), sans méthode, forme, intervalle, système, hypothèses, figure, récurrence, programme, matrices, autres affirmations, point, mode spécial ni arrondi',
        });
      if (lc && m.kernel.lclaims && m.kernel.start.trim() !== m.kernel.lclaims[0]!.expr.trim())
        ctx.addIssue({
          code: 'custom',
          path: ['kernel', 'start'],
          message: '`start` est l’expression de la 1re limite affirmée',
        });
      if (lc && m.kernel.lclaims && m.choices && m.choices.length !== m.kernel.lclaims.length)
        ctx.addIssue({
          code: 'custom',
          path: ['kernel', 'lclaims'],
          message: 'une limite affirmée par choix du QCM, dans le même ordre',
        });
      if (
        cl &&
        (m.kernel.method ||
          m.kernel.form ||
          m.kernel.domain ||
          m.kernel.system ||
          m.kernel.hypotheses?.length ||
          m.kernel.figure ||
          m.kernel.induction ||
          m.kernel.program ||
          m.kernel.matrices ||
          m.complex ||
          m.congruence ||
          m.units ||
          m.bounds ||
          m.geometry ||
          m.rounding ||
          !m.kernel.path?.length)
      )
        ctx.addIssue({
          code: 'custom',
          path: ['kernel'],
          message:
            'une feuille d’affirmations a son corrigé (`path`), sans méthode, forme, intervalle, système, hypothèses, figure, récurrence, programme, matrices, mode spécial ni arrondi',
        });
      if (cl && m.kernel.claims && m.kernel.start.trim() !== m.kernel.claims[0]!.rels[0]!.trim())
        ctx.addIssue({
          code: 'custom',
          path: ['kernel', 'start'],
          message: '`start` est la 1re relation de la 1re affirmation',
        });
      const vr = m.kernel.plugin === 'variations';
      const tb = m.kernel.plugin === 'tables';
      if (!cl && !vr && !tb && !lc && m.kernel.single)
        ctx.addIssue({
          code: 'custom',
          path: ['kernel', 'single'],
          message: '`single` (réponse unique) va avec une feuille d’affirmations',
        });
      if (vr !== (m.kernel.goal.kind === 'study') || vr !== !!m.kernel.vclaims)
        ctx.addIssue({
          code: 'custom',
          path: ['kernel', 'vclaims'],
          message:
            'l’objectif study va avec l’étude d’une fonction (plugin « variations », `vclaims`), et elle seule',
        });
      if (
        vr &&
        (m.kernel.method ||
          m.kernel.form ||
          m.kernel.system ||
          m.kernel.hypotheses?.length ||
          m.kernel.figure ||
          m.kernel.induction ||
          m.kernel.program ||
          m.kernel.matrices ||
          m.kernel.claims ||
          m.complex ||
          m.congruence ||
          m.units ||
          m.bounds ||
          m.geometry ||
          m.rounding ||
          !m.kernel.path?.length)
      )
        ctx.addIssue({
          code: 'custom',
          path: ['kernel'],
          message:
            'une étude de fonction a son corrigé (`path`), sans méthode, forme, système, hypothèses, figure, récurrence, programme, matrices, affirmations, mode spécial ni arrondi',
        });
      if (
        tb !== (m.kernel.goal.kind === 'fill') ||
        tb !== !!m.kernel.tclaims ||
        tb !== !!m.kernel.table
      )
        ctx.addIssue({
          code: 'custom',
          path: ['kernel', 'tclaims'],
          message:
            'l’objectif fill va avec un tableau à remplir (plugin « tables », `table` et `tclaims`), et lui seul',
        });
      if (
        tb &&
        (m.kernel.method ||
          m.kernel.form ||
          m.kernel.domain ||
          m.kernel.system ||
          m.kernel.hypotheses?.length ||
          m.kernel.figure ||
          m.kernel.induction ||
          m.kernel.program ||
          m.kernel.matrices ||
          m.kernel.claims ||
          m.kernel.vclaims ||
          m.complex ||
          m.congruence ||
          m.units ||
          m.bounds ||
          m.geometry ||
          m.rounding ||
          !m.kernel.path?.length ||
          !m.kernel.start.startsWith('⊟'))
      )
        ctx.addIssue({
          code: 'custom',
          path: ['kernel'],
          message:
            'un tableau à remplir part du tableau de l’énoncé (`start` = « ⊟{…} ») et a son corrigé (`path`), sans méthode, forme, intervalle, système, hypothèses, figure, récurrence, programme, matrices, affirmations, mode spécial ni arrondi',
        });
      if (tb && m.kernel.tclaims && m.choices && m.choices.length !== m.kernel.tclaims.length)
        ctx.addIssue({
          code: 'custom',
          path: ['kernel', 'tclaims'],
          message: 'une affirmation par choix du QCM, dans le même ordre',
        });
      if (vr && m.kernel.vclaims && m.choices && m.choices.length !== m.kernel.vclaims.length)
        ctx.addIssue({
          code: 'custom',
          path: ['kernel', 'vclaims'],
          message: 'une affirmation par choix du QCM, dans le même ordre',
        });
      if (cl && m.kernel.claims && m.choices && m.choices.length !== m.kernel.claims.length)
        ctx.addIssue({
          code: 'custom',
          path: ['kernel', 'claims'],
          message: 'une affirmation par choix du QCM, dans le même ordre',
        });
    }
    if (m.kernel && !m.goal.includes(m.kernel.goal.target))
      ctx.addIssue({
        code: 'custom',
        path: ['kernel', 'goal', 'target'],
        message: `l'inconnue ${m.kernel.goal.target} doit être dans goal`,
      });
    m.goal.forEach((g, i) => {
      // feuille d'affirmations (moteur v36), étude de fonction (lot 6.20) : le but est de
      // trancher les choix
      if (
        m.kernel?.plugin === 'claims' ||
        m.kernel?.plugin === 'limclaims' ||
        m.kernel?.plugin === 'variations' ||
        m.kernel?.plugin === 'tables'
      )
        return;
      if (!m.symbols[g])
        ctx.addIssue({ code: 'custom', path: ['goal', i], message: `goal ${g} absent de symbols` });
    });
    Object.entries(m.symbols).forEach(([n, s]) => {
      if (s.given && s.value === undefined && !s.exact?.length)
        ctx.addIssue({
          code: 'custom',
          path: ['symbols', n],
          message: 'grandeur donnée sans value',
        });
    });
  })
  // moteur v41 : « 2ab » se lit 2 × a × b quand a et b sont des lettres de la feuille
  .transform((m) => withNamedProducts(m as Parameters<typeof withNamedProducts>[0]) as typeof m);
export type ScratchModel = z.infer<typeof ScratchModelSchema>;

/** Ce qui est stocké en base pour une question (`scratch_models.model`). */
export const StoredScratchModelSchema = z.object({
  engine_version: z.number().int().min(1),
  model: ScratchModelSchema,
});
export type StoredScratchModel = z.infer<typeof StoredScratchModelSchema>;

/** Le moteur sait-il jouer un modèle validé par cette version ? (même version majeure) */
/** Un modèle d'une version ANTÉRIEURE se joue (rétrocompatibilité) ; une version future, jamais. */
/** Version minimale du moteur pour jouer ce modèle (2 ; 3 avec des congruences ; 4 avec des unités ou de la géométrie ; 5 avec des comparaisons). */
/** La version du moteur qu'il faut pour jouer ce modèle. */
export function modelEngineVersion(model: Parameters<typeof baseEngineVersion>[0]): number {
  let v = baseEngineVersion(model);
  if (usesLongDecimals(model) || usesKernelExpForm(model))
    v = Math.max(v, LONG_DECIMAL_ENGINE_VERSION);
  if (usesKernelClaimsForm(model)) v = Math.max(v, KERNEL_CLAIMS_FORM_ENGINE_VERSION);
  if (usesKernelClaimsProp(model)) v = Math.max(v, KERNEL_CLAIMS_PROP_ENGINE_VERSION);
  if (
    usesNamedProducts(model as Parameters<typeof usesNamedProducts>[0]) ||
    usesKernelPowers(model as Parameters<typeof usesKernelPowers>[0], kernelCubeLine) ||
    usesClaimsPower41(model) ||
    usesKernelSecondDerivative(model) ||
    kernelQuarticDenominator(model)
  )
    v = Math.max(v, KERNEL_POWER_ENGINE_VERSION);
  if (usesKernelSignTable(model)) v = Math.max(v, KERNEL_SIGN_TABLE_ENGINE_VERSION);
  if (usesKernelVariations(model)) v = Math.max(v, KERNEL_VARIATIONS_ENGINE_VERSION);
  if (usesKernelTables(model)) v = Math.max(v, KERNEL_TABLES_ENGINE_VERSION);
  if (usesKernelCanonicalForm(model)) v = Math.max(v, KERNEL_CANONICAL_ENGINE_VERSION);
  if (usesKernelPoint(model)) v = Math.max(v, KERNEL_POINT_ENGINE_VERSION);
  if (usesKernelClaimsLn(model)) v = Math.max(v, KERNEL_CLAIMS_LN_ENGINE_VERSION);
  if (usesKernelFractionForm(model)) v = Math.max(v, KERNEL_FRACTION_FORM_ENGINE_VERSION);
  if (usesKernelV47(model as Parameters<typeof usesKernelV47>[0]))
    v = Math.max(v, KERNEL_V47_ENGINE_VERSION);
  if (usesKernelTest(model)) v = Math.max(v, KERNEL_TEST_ENGINE_VERSION);
  if (usesKernelCondition(model)) v = Math.max(v, KERNEL_CONDITION_ENGINE_VERSION);
  if (usesKernelClaimsLetters(model)) v = Math.max(v, KERNEL_CLAIMS_LETTERS_ENGINE_VERSION);
  if (usesKernelArithPrimes(model)) v = Math.max(v, KERNEL_ARITH_PRIMES_ENGINE_VERSION);
  if (usesKernelMatrixEntry(model) || usesKernelPythonLists(model))
    v = Math.max(v, KERNEL_LISTS_ENGINE_VERSION);
  if (usesKernelFractions(model)) v = Math.max(v, KERNEL_FRACTIONS_ENGINE_VERSION);
  if (usesKernelLimit(model)) v = Math.max(v, KERNEL_LIMIT_ENGINE_VERSION);
  if (usesKernelIntegral(model)) v = Math.max(v, KERNEL_INTEGRAL_ENGINE_VERSION);
  if (usesKernelLimitPlus(model)) v = Math.max(v, KERNEL_LIMIT_PLUS_ENGINE_VERSION);
  if (usesKernelLimitClaims(model)) v = Math.max(v, KERNEL_LIMIT_CLAIMS_ENGINE_VERSION);
  if (usesKernelLimitDL(model)) v = Math.max(v, KERNEL_LIMIT_DL_ENGINE_VERSION);
  if (usesKernelRiemann(model)) v = Math.max(v, KERNEL_RIEMANN_ENGINE_VERSION);
  if (usesKernelArc(model)) v = Math.max(v, KERNEL_ARC_ENGINE_VERSION);
  if (usesKernelEquiv(model)) v = Math.max(v, KERNEL_EQUIV_ENGINE_VERSION);
  if (usesKernelOde(model)) v = Math.max(v, KERNEL_ODE_ENGINE_VERSION);
  if (usesKernelOdePlus(model)) v = Math.max(v, KERNEL_ODE_PLUS_ENGINE_VERSION);
  if (usesKernelPartial(model)) v = Math.max(v, KERNEL_PARTIAL_ENGINE_VERSION);
  const declared = (model as { kernel?: { minEngine?: number } }).kernel?.minEngine;
  if (declared) v = Math.max(v, declared);
  return v;
}

function baseEngineVersion(model: {
  congruence?: unknown;
  units?: unknown;
  geometry?: unknown;
  checks?: unknown[];
  rounding?: unknown;
  complex?: unknown;
  preds?: unknown[];
  bounds?: unknown;
  stepwise?: boolean;
  path?: unknown[];
  kernel?: unknown;
  traps?: unknown[];
  choices?: unknown[];
}): number {
  // affirmations à trancher (moteur v36)
  if (usesKernelClaims(model))
    return usesKernelClaimsPlus(model)
      ? KERNEL_CLAIMS_PLUS_ENGINE_VERSION
      : KERNEL_CLAIMS_ENGINE_VERSION;
  // arithmétique : Euclide, Bézout (moteur v35)
  if (usesKernelArith(model)) return KERNEL_ARITH_ENGINE_VERSION;
  // modes spéciaux passés au noyau (moteur v34)
  if (usesKernelSpecial(model)) return KERNEL_SPECIAL_ENGINE_VERSION;
  // écriture à développer ou à factoriser (moteur v33)
  if (usesKernelForm(model)) return KERNEL_FORM_ENGINE_VERSION;
  // feuille à formules du noyau (moteur v32)
  if (usesKernelFormula(model)) return KERNEL_FORMULA_ENGINE_VERSION;
  // dérivées et primitives (moteur v31)
  if (usesKernelAnalysis(model)) return KERNEL_ANALYSIS_ENGINE_VERSION;
  // calcul matriciel (moteur v30)
  if (usesKernelMatrix(model)) return KERNEL_MATRIX_ENGINE_VERSION;
  // trace d'un programme Python (moteur v28) : « != », « ! »… sont du code, pas des factorielles
  if (usesKernelPython(model))
    return usesKernelPythonPlus(model as Record<string, unknown>)
      ? KERNEL_PYTHON_PLUS_ENGINE_VERSION
      : KERNEL_PYTHON_ENGINE_VERSION;
  // « k parmi n » (moteur v27) : quel que soit le mode, une app v26 ne le lit pas
  if (usesParmi(model as Record<string, unknown>)) return KERNEL_COUNT_ENGINE_VERSION;
  if (model.kernel)
    return usesKernelCount(model)
      ? KERNEL_COUNT_ENGINE_VERSION
      : usesKernelRecurrence(model)
        ? KERNEL_RECURRENCE_ENGINE_VERSION
        : usesKernelGeometry(model)
          ? KERNEL_GEOMETRY_ENGINE_VERSION
          : usesKernelTrig(model)
            ? KERNEL_TRIG_ENGINE_VERSION
            : usesKernelPoly(model)
              ? KERNEL_POLY_ENGINE_VERSION
              : usesKernelSystem(model)
                ? KERNEL_SYSTEM_ENGINE_VERSION
                : usesKernelAngle(model)
                  ? KERNEL_ANGLE_ENGINE_VERSION
                  : usesKernelRadical(model)
                    ? KERNEL_RADICAL_ENGINE_VERSION
                    : usesKernelExpLog(model)
                      ? KERNEL_EXPLOG_ENGINE_VERSION
                      : KERNEL_ENGINE_VERSION;
  if (/\barc(cos|sin|tan)\b/.test(JSON.stringify(model))) return ARCTRIG_ENGINE_VERSION;
  if (model.path?.length) return PATH_ENGINE_VERSION;
  if (model.stepwise) return STEPWISE_ENGINE_VERSION;
  if (model.bounds) {
    const b = model.bounds as { lower?: string; upper?: string };
    return b.lower === undefined || b.upper === undefined
      ? ONE_BOUND_ENGINE_VERSION
      : BOUNDS_ENGINE_VERSION;
  }
  if (usesDoubleRoot(model.preds)) return ODD_ROOT_ENGINE_VERSION;
  if (model.complex) return COMPLEX_ENGINE_VERSION;
  if (model.rounding) return ROUNDING_ENGINE_VERSION;
  if (usesCounting(model as Record<string, unknown>)) return COUNTING_ENGINE_VERSION;
  if (model.checks?.length) return CHECKS_ENGINE_VERSION;
  if (model.units || model.geometry) return UNITS_ENGINE_VERSION;
  return model.congruence ? CONGRUENCE_ENGINE_VERSION : 2;
}
/** un outil « second degré » avec la solution double (Δ = 0, moteur v12) ? */
function usesDoubleRoot(preds: unknown[] | undefined): boolean {
  return (preds ?? []).some(
    (p) =>
      !!p &&
      typeof p === 'object' &&
      (p as { skill?: string; double?: string }).skill === 'secondDegre' &&
      !!(p as { double?: string }).double,
  );
}
export function isEngineCompatible(engineVersion: number): boolean {
  return Number.isInteger(engineVersion) && engineVersion >= 1 && engineVersion <= ENGINE_VERSION;
}
